// Company onboarding UI — piece 3 of docs/design/company-onboarding.md.
// Lane: node/fix-r21b-draft-edits-discarded (closes R20O-1).
// Consumes POST/PATCH /v1/admin/companies and signed opening-document upload.
// Does not touch pipeline/opening/ or contracts/.
//
// Owner rule 2026-08-01: no silent determination; anchoring corollary —
// source text beside every derived determination, never pre-select Confirm,
// correcting as cheap as confirming. Owner 2026-08-10: Confirm refused when
// source text is absent/blank/marker; required = QB trio; optional docs need
// attributed acknowledgement; drafts listable/resumable/abandonable in UI.
// Owner 2026-08-11 (option C): honest surface + wire basis through promote NOW;
// durable server-side determinations deferred past live beta — no determinations
// field on submit. Reject does not unlock Submit. Form basis and books_basis
// are one write. Session-local dispositions must not be labelled as if durable.
// Owner decision fix-r21c (R20O-3 / R20O-1 w8qz): block forever when the AM
// cannot quote — `no_source` records the honest look without unlocking Submit.
// An attested unlock that travels with promote is GM-1 and is not authorised.
// Owner 2026-08-15 (issue #604): Completing accrual is recorded as evidence
// and cannot pass quietly. Operating on accrual is refused until the close
// pack exists. Do not write companies.basis / company.yaml from that Completing.
//
// R20O-1: after Save draft the form keeps taking edits that Present shows and
// Submit gates on — promote must not fall back to a stale draft row for any
// form-controlled field. Submit sends the whole promote-writable snapshot
// (CompanyUpdate already declares every field; no contract widen). Slug is the
// promote key and cannot travel — lock it once a draft session exists.
//
// FLAG: no DELETE company-drafts route exists. Browser sessionStorage is still
// the local inventory for documents and abandon; GET /v1/admin/company-drafts
// lists server rows so a draft minted from the untracked-counterparty card
// is visible. Abandon still removes only the local row.

import {
  ACCRUAL_PACK_ISSUE,
  operationalAccrualPackReady,
} from "../../../ingest-worker/src/accrual_pack_capability";
import {
  amVisibleDisplayText,
  amVisibleMultilineDisplayText,
  amVisibleSourceNeedsHazardNote,
} from "../../../ingest-worker/src/am_visible_text";
import type { AdminClient } from "../api.js";
import { AdminApiError } from "../api.js";
import { clear, h, safeText } from "../dom.js";
import {
  determinationsFromExtract,
  extractStatusCopy,
  EXTRACT_STATUS_LOAD_ERROR_COPY,
  ocrCropOffered,
  ocrPollShouldContinue,
  OCR_POLL_INTERVAL_MS,
  OCR_POLL_MAX_TICKS,
  OCR_SCAN_MANUAL_CONFIRM_REFUSED_COPY,
  OCR_SCAN_MANUAL_QUOTE_NONE_COPY,
  ocrStatusCopy,
} from "../opening_extract_map.js";
import { locationHintFor, displayFormType, canonicalFormType, OPENING_FORM_TYPES } from "../opening_location_hints.js";
import { button } from "../ui/components.js";
import { advice } from "../ui/capabilities.js";
import * as face from "../ui/face.js";
import { createCompanyDirectory, type CompanyDirectory } from "../ui/face.js";
import {
  AuthoredRefusal,
  failureCopy,
  isUnreachable,
  type FailureCopy,
} from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { navLabel } from "../ui/sentences.js";
import { runWrite } from "../ui/write.js";
import { timeAgoText } from "../ui/display-words.js";
import type {
  Company,
  CompanyBasis,
  CompanyCreate,
  CompanyDraft,
  CompanyFeaturePack,
  CompanyType,
  CompanyUpdate,
  DerivedDetermination,
  DeterminationDecisionKind,
  OpeningDeterminationPut,
  OpeningDocumentExtractResponse,
  OpeningDocumentKind,
  OpeningDocumentOcrResponse,
  OpeningDocumentUploadResponse,
  OpeningOcrDeterminationField,
  OpeningDocumentQuoteAssertion,
  QuoteCheck,
  QuotationProvenance,
  QuoteVerificationStatus,
  StaffRole,
  CompanySlug,
} from "../types.js";
import {
  OPENING_DOCUMENT_KINDS,
  OPENING_OPTIONAL_DOCUMENT_KINDS,
  OPENING_REQUIRED_DOCUMENT_KINDS,
  quoteCheckIsCompleting,
} from "../types.js";

export type { DeterminationDecisionKind, QuotationProvenance };

/**
 * Flip to false if the signed opening-document upload route is ever removed.
 * Keeps the page honest: controls stay disabled and the missing-route flag
 * resurfaces. Production default is true — the route shipped with this lane.
 */
export const OPENING_DOCUMENT_UPLOAD_ROUTE_AVAILABLE = true;

/**
 * Matches `pipeline.opening.return_extract.VERBATIM_SOURCE_UNAVAILABLE`.
 * Confirm is refused when sourceText equals this (or is blank).
 */
export const SOURCE_TEXT_UNAVAILABLE =
  "no source text available for this reading";

/** Shown beside a quotation when hazards were replaced for display. */
export const SOURCE_HAZARD_NOTE =
  "Control characters in this quotation are shown as \uFFFD so they cannot hide or reorder the text. The stored quotation is unchanged.";

function sourceQuotationDisplay(sourceText: string): {
  text: string;
  note: HTMLParagraphElement | null;
} {
  const text = amVisibleMultilineDisplayText(sourceText);
  if (!amVisibleSourceNeedsHazardNote(sourceText)) {
    return { text, note: null };
  }
  return {
    text,
    note: h(
      "p",
      { class: "muted", "data-role": "determination-source-hazard-note" },
      [SOURCE_HAZARD_NOTE],
    ),
  };
}

const DRAFT_STORAGE_KEY = "gv-books-onboarding-drafts-v1";

/**
 * One write namespace per mounted page and per rendered card. Keys are stable
 * for that page (so a repaint retry shares the first flight) but never shared
 * with another mounted page or another card (B3a-3). They are internal ids,
 * never words, so they carry no company slug.
 */
let pageWriteSeq = 0;
let pressWriteSeq = 0;
const pressWriteKeys = new WeakMap<HTMLElement, number>();

export interface OnboardingPageOptions {
  client: AdminClient;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /**
   * Injection for derived determinations (tests / future extract feed).
   * When omitted, {@link produceDerivedDeterminations} builds marker
   * proposals from uploaded equity-feeding documents so the panel is not
   * permanently empty. Never invents Confirm-ready quotes.
   */
  derivedDeterminations?: readonly DerivedDetermination[];
  /**
   * Override for the upload-route availability gate (tests). Defaults to
   * {@link OPENING_DOCUMENT_UPLOAD_ROUTE_AVAILABLE}.
   */
  openingDocumentUploadRouteAvailable?: boolean;
  /** Override draft persistence (tests). Defaults to sessionStorage. */
  draftStore?: DraftStore;
  /** Guided onboarding: open the new Add company flow (OB-3). */
  openAddCompany?: () => void;
  /** Guided onboarding: open one company's checklist (OB-3). */
  openChecklist?: (slug: CompanySlug) => void;
  /**
   * Called once after a draft is registered as a company, with the slug the
   * server returned. Optional and backward compatible: the shell (main.ts,
   * lane R8c) passes `reloadCompanies` so the company switcher lists the new
   * company. Do not rename — R8c wires this exact name.
   *
   * R8c passes `reloadCompanies`, which is async, so the return value may be a
   * promise and a rejection is handled (round 3 review).
   */
  onRegistered?: (slug: CompanySlug) => void | Promise<void>;
  /**
   * Who is looking. Owner attest is the owner's: the Worker refuses
   * `ownerAttest: true` for anyone else. Anything but "owner" (including a
   * caller that does not say) is offered no attest control.
   */
  role?: StaffRole;
}

/** Session-local record of an uploaded opening document (metadata only). */
export interface DraftDocumentRow {
  kind: OpeningDocumentKind;
  filename: string;
  contentSha256Hex: string;
  /** R2 object key when a signed upload completed; null if only listed. */
  r2Key: string | null;
  /** Law 1 — GL detail never rendered; link/list only. */
  quarantine: boolean;
  uploadedAt: string;
}

/** Attributed acknowledgement that an optional document is missing. */
export interface OptionalDocumentAck {
  kind: (typeof OPENING_OPTIONAL_DOCUMENT_KINDS)[number];
  acknowledgedBy: string;
  acknowledgedAt: string;
  note: string;
}

/**
 * Completing events are PUT at click; this local copy is for paint.
 * Reject and no_source are remembered so they do not silently vanish, but
 * neither completes the reading — Correct (with a document-verified
 * quotation) must follow. no_source is the honest "I looked and cannot quote"
 * disposition; it still blocks Submit (block forever).
 * Completing requires a QuoteCheck that Completing (extract/qb hit or OCR waiver).
 */
export interface DeterminationDecision {
  field: string;
  decision: DeterminationDecisionKind;
  correctedValue?: string;
  /** Canonical determined value on Completing (1120S not 1120-S). */
  determinedValue?: string;
  /** Engine candidate snapshotted at click. */
  engineCandidate?: string | null;
  /** Quotation used at decision time (proposal span or AM-pasted). */
  sourceTextUsed: string;
  decidedAt: string;
  /**
   * Required for no_source — what the AM checked. Pattern matches
   * OptionalDocumentAck.note (attributed, browser-local).
   */
  note?: string;
  /** Set for no_source — draft.createdBy at decision time. */
  acknowledgedBy?: string;
  /**
   * Set on Completing dispositions. am_asserted labels paste so it cannot
   * render as an engine extract.
   */
  quotationProvenance?: QuotationProvenance;
  /**
   * Completing only after Worker QuoteCheck Completing (verified extract/qb
   * or OCR waiver asserted).
   */
  quoteVerification?: QuoteVerificationStatus;
  /**
   * Binding used for document identity on PUT.
   * Required when quoteVerification is verified or asserted.
   */
  quoteAssertion?: OpeningDocumentQuoteAssertion;
  quoteCheck?: QuoteCheck;
}

/** True when a string is a company books basis promote can write. */
export function isCompanyBasisValue(value: string): value is CompanyBasis {
  return value === "cash" || value === "accrual";
}

/**
 * Resolve the company `basis` a books_basis confirm/correct implies.
 * Returns null for reject, non-basis text, or other fields.
 */
export function resolvedBooksBasisValue(
  decision: DeterminationDecision,
  proposal?: DerivedDetermination,
): CompanyBasis | null {
  if (decision.field !== "books_basis") return null;
  if (decision.decision === "correct") {
    const raw = (decision.correctedValue ?? "").trim().toLowerCase();
    return isCompanyBasisValue(raw) ? raw : null;
  }
  if (decision.decision === "confirm") {
    const raw = (proposal?.candidate ?? "").trim().toLowerCase();
    return isCompanyBasisValue(raw) ? raw : null;
  }
  return null;
}

/** Tracking issue every #604 record this panel creates must name. */
export const ACCRUAL_NOT_IMPLEMENTED_ISSUE = ACCRUAL_PACK_ISSUE;

export const ACCRUAL_NOT_IMPLEMENTED_TITLE =
  "Accrual books are not built yet";

export const ACCRUAL_NOT_IMPLEMENTED_COPY =
  "The accrual engine is not built yet. If you continue, this company's books will be kept on a cash basis until it is. This is recorded for review and must be resolved before the first close for this company.";

/**
 * Going-forward accrual writes. Consults the same TypeScript capability as
 * ingest-worker (`operationalAccrualPackReady` in accrual_pack_capability.ts),
 * which reads accrual_close_pack.ts. Python is the language twin, bound by
 * `examine_accrual_pack_language_drift` — the portal cannot import it.
 *
 * Why this import: the portal is a browser surface. It cannot import
 * `accrual_pack.ts` (HttpError / `@gv/contracts`) or Python. The capability
 * module has no Worker deps — the same pattern as queue.ts importing
 * `review_queue_blockers.ts`. No contract field (no grant). No default
 * argument, query-param, or declarations bag: a future call site cannot
 * open this from untrusted input. Completing evidence is independent.
 */
export function operationalAccrualWritesAllowed(): boolean {
  return operationalAccrualPackReady();
}

export type CompletingCompanyBasisExamination =
  | { ok: true; basis: CompanyBasis; examined: string }
  | { ok: false; examined: string };

/**
 * Company `basis` a Completing determination would write.
 * Reuses `resolvedBooksBasisValue` for the books_basis alias; does not
 * re-derive that mapping. Fail-closed: missing or unparseable → ok:false
 * and prints what was examined.
 */
export function examineCompletingCompanyBasis(args: {
  field: string;
  decision: DeterminationDecisionKind;
  determinedValue: string | null;
  engineCandidate: string | null;
  correctedValue?: string;
  proposal?: DerivedDetermination;
}): CompletingCompanyBasisExamination {
  const examined = [
    `field=${args.field}`,
    `decision=${args.decision}`,
    `determinedValue=${args.determinedValue ?? "null"}`,
    `engineCandidate=${args.engineCandidate ?? "null"}`,
    `correctedValue=${args.correctedValue ?? "unset"}`,
  ].join(" ");
  if (args.field !== "basis" && args.field !== "books_basis") {
    return { ok: false, examined };
  }
  if (!isCompletingDisposition(args.decision)) {
    return { ok: false, examined };
  }
  if (args.field === "books_basis") {
    const resolved = resolvedBooksBasisValue(
      {
        field: "books_basis",
        decision: args.decision,
        ...(args.correctedValue !== undefined
          ? { correctedValue: args.correctedValue }
          : {}),
        sourceTextUsed: "",
        decidedAt: "",
      },
      args.proposal,
    );
    if (resolved !== null) {
      return { ok: true, basis: resolved, examined };
    }
    console.error("ACCRUAL_PACK_REFUSED", examined);
    return { ok: false, examined };
  }
  // Correct writes the AM value; Confirm writes the engine candidate.
  // Prefer correctedValue on correct so an examiner that still holds the
  // engine candidate cannot classify an accrual correction as cash.
  const rawSource =
    args.decision === "correct"
      ? (args.correctedValue ?? args.determinedValue ?? args.engineCandidate)
      : (args.determinedValue ?? args.engineCandidate);
  const raw = (rawSource ?? "").trim().toLowerCase();
  if (isCompanyBasisValue(raw)) {
    return { ok: true, basis: raw, examined };
  }
  console.error("ACCRUAL_PACK_REFUSED", examined);
  return { ok: false, examined };
}

/** Confirm/Correct complete a reading; Reject and no_source do not. */
export function isCompletingDisposition(
  decision: DeterminationDecisionKind,
): boolean {
  return decision === "confirm" || decision === "correct";
}

/**
 * Completing that the Submit gate accepts.
 * Extract-span Confirm is `verified`. OCR-crop Completing is `asserted`
 * (AM-asserted; still requires a quoteAssertion for promote).
 */
export function isVerifiedCompletingDecision(
  decision: DeterminationDecision,
): boolean {
  return (
    isCompletingDisposition(decision.decision) &&
    (decision.quoteVerification === "verified" ||
      decision.quoteVerification === "asserted") &&
    decision.quoteAssertion !== undefined
  );
}

/** Non-completing dispositions that still occupy the proposal slot. */
export function isBlockingDisposition(
  decision: DeterminationDecisionKind,
): boolean {
  return decision === "reject" || decision === "no_source";
}

/**
 * Field → document kinds that may bind a Completing quote for that field.
 *
 * Mirrors ingest-worker `DETERMINATION_QUOTE_FIELD_KINDS` (same export name +
 * shape). Worker promote enforces; portal filters Apply candidates. Drift is
 * CI-enforced by `tests/test_quote_field_kinds_lockstep.py`. Not in
 * packages/contracts — no contract change authorised for fix-pgs21a.
 */
export const DETERMINATION_QUOTE_FIELD_KINDS = {
  form: ["tax-return"],
  tax_year: ["tax-return"],
  basis: ["tax-return"],
  books_basis: [
    "qb-trial-balance",
    "qb-balance-sheet",
    "qb-profit-and-loss",
  ],
} as const;

/**
 * Opening documents that can bind a quote for a determination field.
 * Driven by {@link DETERMINATION_QUOTE_FIELD_KINDS} — tax fields → tax-return
 * only; books_basis → the QB trio (not collapsed).
 */
export function documentsForDeterminationField(
  field: string,
  documents: readonly DraftDocumentRow[],
): DraftDocumentRow[] {
  const uploaded = documents.filter((d) => d.r2Key !== null);
  if (
    field !== "form" &&
    field !== "tax_year" &&
    field !== "basis" &&
    field !== "books_basis"
  ) {
    return [];
  }
  const allowed: readonly string[] = DETERMINATION_QUOTE_FIELD_KINDS[field];
  return uploaded.filter((d) => allowed.includes(d.kind));
}

/** Build promote quoteAssertions from verified Completing decisions. */
export function quoteAssertionsFromDecisions(
  decisions: readonly DeterminationDecision[],
): OpeningDocumentQuoteAssertion[] {
  const out: OpeningDocumentQuoteAssertion[] = [];
  for (const d of decisions) {
    if (!isVerifiedCompletingDecision(d)) continue;
    if (d.quoteAssertion === undefined) continue;
    if (d.quoteAssertion.quote.trim().length === 0) continue;
    out.push(d.quoteAssertion);
  }
  return out;
}

/** Live company-form values the Present panel and Submit gate share. */
export interface FormState {
  slug: CompanySlug;
  displayName: string;
  companyType: CompanyType | "";
  basis: CompanyBasis | "";
  featurePacks: CompanyFeaturePack[];
  hubLink: string;
  /** True when the AM explicitly set hubLink to null (empty). */
  hubLinkStated: boolean;
}

/**
 * Promote-writable company fields derived from the live form.
 *
 * Every field promoteDraft can take from the PATCH (displayName, companyType,
 * basis, featurePacks, hubLink) is produced here together. Submit always
 * sends this whole set so an omitted key cannot silently revive a stale
 * draft-row value — the class defect behind R20O-1, not a per-field wire.
 * Returns null when the form is not yet determinate enough to promote.
 */
export function companyWriteFieldsFromForm(state: FormState): {
  displayName: string;
  companyType: CompanyType;
  basis: CompanyBasis;
  featurePacks: CompanyFeaturePack[];
  hubLink: string | null;
} | null {
  if (state.companyType === "") return null;
  if (state.basis === "" || !isCompanyBasisValue(state.basis)) return null;
  if (state.featurePacks.length === 0) return null;
  if (!state.displayName.trim()) return null;
  return {
    displayName: state.displayName.trim(),
    companyType: state.companyType,
    basis: state.basis,
    featurePacks: [...state.featurePacks],
    // Same rule as Save draft: non-practice clears hub; practice keeps trim
    // or null. Always include the key so a practice→external edit nulls the
    // stale annotation instead of leaving draft.hub_link in place.
    hubLink:
      state.companyType === "gvpm-practice"
        ? state.hubLink.trim() || null
        : null,
  };
}

/** PATCH body for Submit — full form snapshot + submit:true. Completing
 * events were already PUT; quoteAssertions is not a second Completing path. */
export function promotePatchFromForm(
  state: FormState,
  _decisions: readonly DeterminationDecision[] = [],
): CompanyUpdate | null {
  void _decisions;
  const fields = companyWriteFieldsFromForm(state);
  if (fields === null) return null;
  return {
    submit: true,
    ...fields,
  };
}

interface DraftSession {
  draft: CompanyDraft;
  documents: DraftDocumentRow[];
  optionalAcks: OptionalDocumentAck[];
  decisions: DeterminationDecision[];
}

/** Persisted draft inventory entry (browser-local; see FLAG above). */
export interface PersistedDraftInventoryEntry {
  draft: CompanyDraft;
  documents: DraftDocumentRow[];
  optionalAcks: OptionalDocumentAck[];
  decisions: DeterminationDecision[];
}

export interface DraftStore {
  list(): PersistedDraftInventoryEntry[];
  upsert(entry: PersistedDraftInventoryEntry): void;
  remove(slug: CompanySlug): void;
}

const COMPANY_TYPES: readonly CompanyType[] = [
  "gv-internal",
  "gvpm-practice",
  "external",
];
const BASES: readonly CompanyBasis[] = ["cash", "accrual"];
const PACKS: readonly CompanyFeaturePack[] = ["core", "medical"];

const FACE_FIELD: Readonly<Record<string, string>> = {
  slug: "Web address",
  displayName: "Display name",
  companyType: "Company type",
  basis: "Books basis",
  books_basis: "Books basis",
  featurePacks: "What this company does",
  hubLink: "Practice connection",
  form: "Tax form",
  tax_year: "Tax year",
};

const FACE_DOC: Readonly<Record<string, string>> = {
  "qb-trial-balance": "QuickBooks trial balance",
  "qb-balance-sheet": "QuickBooks balance sheet",
  "qb-profit-and-loss": "QuickBooks profit and loss",
  "qb-general-ledger": "QuickBooks general ledger",
  "tax-return": "Tax return",
  "bank-statement": "Bank statement",
  "loan-document": "Loan document",
  other: "Other document",
};

const FACE_VALUE: Readonly<Record<string, string>> = {
  "gv-internal": "Internal company",
  "gvpm-practice": "Medical practice",
  external: "Outside company",
  cash: "Cash",
  accrual: "Accrual",
  core: "Everyday books",
  medical: "Medical",
};

/** Kinds a client can legitimately have several of (rd round 11, R6-06). */
const MULTI_FILE_DOCUMENT_KINDS: readonly OpeningDocumentKind[] = [
  "bank-statement",
  "loan-document",
  "other",
];

function showDetails(detail: string): HTMLElement {
  return h("details", { class: "disclosure", "data-role": "show-details" }, [
    h("summary", {}, ["Show details"]),
    h("p", {}, [detail]),
  ]);
}

function faceField(key: string): string {
  return FACE_FIELD[key] ?? "This item";
}

function faceDoc(kind: string): string {
  return FACE_DOC[kind] ?? "This document";
}

function faceValue(value: string): string {
  return FACE_VALUE[value] ?? "This value";
}

/**
 * How one determined value reads in a sentence, by field. Shared by the 409
 * read-back and the success banner so the two cannot disagree about a value in
 * the same field (round 3 review). Unknown closed-set values fall back to the
 * recorded text rather than "This value": hiding what was written is worse.
 */
function determinationValueFace(field: string, value: string): string {
  const raw = value.trim();
  const shown =
    field === "form"
      ? displayFormType(raw)
      : field === "basis" || field === "books_basis"
        ? (FACE_VALUE[raw] ?? raw)
        : raw;
  return shown === "" ? "no value" : amVisibleDisplayText(shown);
}

const SLUG_RE = /^[a-z][a-z0-9-]{0,63}$/;

/** Type guard — kind select values are untrusted strings. */
export function isOpeningDocumentKind(
  value: string,
): value is OpeningDocumentKind {
  return (OPENING_DOCUMENT_KINDS as readonly string[]).includes(value);
}

/** Quarantined kinds — Law 1; page lists/links, never renders GL detail. */
export function isQuarantineKind(kind: OpeningDocumentKind): boolean {
  return kind === "qb-general-ledger";
}

/**
 * True when sourceText is a usable document quotation for Confirm.
 * Blank, whitespace-only, and the unavailable marker all refuse Confirm.
 */
export function hasUsableSourceText(sourceText: string): boolean {
  const trimmed = sourceText.trim();
  return trimmed.length > 0 && trimmed !== SOURCE_TEXT_UNAVAILABLE;
}

/**
 * Missing required determinations for create/submit. Empty when complete.
 * hubLink must be stated (null or value); non-null only for gvpm-practice.
 */
export function missingDeterminations(state: FormState): string[] {
  const missing: string[] = [];
  if (state.slug === "" || !SLUG_RE.test(state.slug)) {
    missing.push("slug");
  }
  if (!state.displayName.trim()) {
    missing.push("displayName");
  }
  if (state.companyType === "") {
    missing.push("companyType");
  }
  if (state.basis === "") {
    missing.push("basis");
  }
  if (state.featurePacks.length === 0) {
    missing.push("featurePacks");
  }
  if (state.companyType === "gvpm-practice") {
    // hubLink key is required; value may be a practice id. Empty string with
    // hubLinkStated means the AM has not yet provided one — still missing.
    if (!state.hubLinkStated || state.hubLink.trim() === "") {
      missing.push("hubLink");
    }
  } else if (state.companyType !== "" && state.hubLink.trim() !== "") {
    // Non-medical with a hub link is invalid — named so the AM can clear it.
    missing.push("hubLink (only valid for gvpm-practice)");
  }
  return missing;
}

/** Required opening-document kinds still absent from the draft inventory. */
export function missingDocumentKinds(
  documents: readonly DraftDocumentRow[],
): OpeningDocumentKind[] {
  const present = new Set(
    documents.filter((d) => d.r2Key !== null).map((d) => d.kind),
  );
  return OPENING_REQUIRED_DOCUMENT_KINDS.filter((k) => !present.has(k));
}

/** Optional kinds absent and not yet acknowledged. */
export function missingOptionalDocumentKinds(
  documents: readonly DraftDocumentRow[],
  acks: readonly OptionalDocumentAck[],
): OpeningDocumentKind[] {
  const present = new Set(
    documents.filter((d) => d.r2Key !== null).map((d) => d.kind),
  );
  const acked = new Set(acks.map((a) => a.kind));
  return OPENING_OPTIONAL_DOCUMENT_KINDS.filter(
    (k) => !present.has(k) && !acked.has(k),
  );
}

/**
 * Portal-local determination producer. Injected feed (tests) wins and keeps
 * citation / extractStatus / locationHint. Otherwise a tax-return extract
 * view maps to cited proposals; without one, marker proposals still carry
 * form-structure location hints so an unreadable scan is not a blank card.
 */
export function produceDerivedDeterminations(
  documents: readonly DraftDocumentRow[],
  injected?: readonly DerivedDetermination[],
  extractView?: OpeningDocumentExtractResponse | null,
): DerivedDetermination[] {
  if (injected !== undefined) {
    return injected.map((d) => {
      if (d.locationHint !== undefined) return d;
      const hint = locationHintFor(d.field);
      return hint !== null ? { ...d, locationHint: hint } : d;
    });
  }
  const present = new Set(
    documents.filter((d) => d.r2Key !== null).map((d) => d.kind),
  );
  const out: DerivedDetermination[] = [];
  if (present.has("tax-return")) {
    if (extractView !== undefined && extractView !== null) {
      out.push(...determinationsFromExtract(extractView));
    } else {
      for (const field of ["form", "tax_year", "basis"] as const) {
        const hint = locationHintFor(field);
        out.push({
          field,
          candidate: "unextracted — open the return and state the reading",
          sourceText: SOURCE_TEXT_UNAVAILABLE,
          ...(hint !== null ? { locationHint: hint } : {}),
        });
      }
    }
  }
  const qbTrio =
    present.has("qb-trial-balance") &&
    present.has("qb-balance-sheet") &&
    present.has("qb-profit-and-loss");
  if (qbTrio) {
    out.push({
      field: "books_basis",
      candidate: "unextracted — read the sealed QB report basis stamp",
      sourceText: SOURCE_TEXT_UNAVAILABLE,
    });
  }
  return out;
}

/** Fields whose proposals are invalidated when a document kind is replaced. */
function fieldsTiedToDocumentKind(kind: OpeningDocumentKind): string[] {
  if (kind === "tax-return") return ["form", "tax_year", "basis"];
  if (
    kind === "qb-trial-balance" ||
    kind === "qb-balance-sheet" ||
    kind === "qb-profit-and-loss"
  ) {
    return ["books_basis"];
  }
  return [];
}

export interface DraftCompletenessArgs {
  state: FormState;
  documents: readonly DraftDocumentRow[];
  optionalAcks: readonly OptionalDocumentAck[];
  proposals: readonly DerivedDetermination[];
  decisions: readonly DeterminationDecision[];
}

/**
 * Every gap the submit gate and the Missing panel must agree on.
 * Display and act decide over this single set (PGS18-2) — do not rebuild
 * the list in a second place.
 */
export interface DraftGaps {
  determinations: string[];
  documents: OpeningDocumentKind[];
  optionalDocuments: OpeningDocumentKind[];
  /**
   * Proposal field names with no Completing disposition yet (Reject and
   * no_source do not count as Completing).
   */
  undisposedProposals: string[];
  /**
   * Proposals the AM Rejected — still blocking. Reject is not a resolved
   * equity reading; Correct is required (owner 2026-08-11 / R18O-2).
   */
  refusedProposals: string[];
  /**
   * Proposals where the AM recorded no_source — still blocking. Honest look
   * without a quotation does not unlock Submit (fix-r21c / block forever).
   */
  noSourceProposals: string[];
  /**
   * books_basis Confirm/Correct resolved to cash|accrual but form `basis`
   * disagrees — promote would write the form. Must be empty before Submit.
   */
  basisDisagreements: string[];
}

export function draftGaps(args: DraftCompletenessArgs): DraftGaps {
  const completing = new Set(
    args.decisions
      .filter((d) => isVerifiedCompletingDecision(d))
      .map((d) => d.field),
  );
  const noSource = args.decisions
    .filter((d) => d.decision === "no_source")
    .filter((d) => args.proposals.some((p) => p.field === d.field))
    .filter((d) => !completing.has(d.field))
    .map((d) => d.field);
  const noSourceSet = new Set(noSource);
  const refused = args.decisions
    .filter((d) => d.decision === "reject")
    .filter((d) => args.proposals.some((p) => p.field === d.field))
    // A later Confirm/Correct for the same field supersedes Reject/no_source.
    .filter((d) => !completing.has(d.field))
    .filter((d) => !noSourceSet.has(d.field))
    .map((d) => d.field);
  const refusedSet = new Set(refused);

  const basisDisagreements: string[] = [];
  const booksDecision = args.decisions.find(
    (d) =>
      d.field === "books_basis" && isVerifiedCompletingDecision(d),
  );
  if (booksDecision) {
    const proposal = args.proposals.find((p) => p.field === "books_basis");
    const resolved = resolvedBooksBasisValue(booksDecision, proposal);
    if (
      resolved !== null &&
      args.state.basis !== "" &&
      args.state.basis !== resolved
    ) {
      // Completing accrual is evidence. While operational accrual is refused,
      // form.basis may stay cash — that is the split, not a Submit gap.
      if (
        resolved === "accrual" &&
        !operationalAccrualWritesAllowed()
      ) {
        // skip
      } else {
        basisDisagreements.push(
          `basis is ${args.state.basis} but books_basis disposition is ${resolved}`,
        );
      }
    }
  }

  return {
    determinations: missingDeterminations(args.state),
    documents: missingDocumentKinds(args.documents),
    optionalDocuments: missingOptionalDocumentKinds(
      args.documents,
      args.optionalAcks,
    ),
    undisposedProposals: args.proposals
      .filter(
        (p) =>
          !completing.has(p.field) &&
          !refusedSet.has(p.field) &&
          !noSourceSet.has(p.field),
      )
      .map((p) => p.field),
    refusedProposals: refused,
    noSourceProposals: noSource,
    basisDisagreements,
  };
}

export function draftGapsEmpty(gaps: DraftGaps): boolean {
  return (
    gaps.determinations.length === 0 &&
    gaps.documents.length === 0 &&
    gaps.optionalDocuments.length === 0 &&
    gaps.undisposedProposals.length === 0 &&
    gaps.refusedProposals.length === 0 &&
    gaps.noSourceProposals.length === 0 &&
    gaps.basisDisagreements.length === 0
  );
}

/**
 * Full submit gate: form determinations + required docs + optional acks +
 * every derived proposal disposed. Documents are consulted (R17O-1).
 * Delegates to {@link draftGaps} so the Missing panel cannot drift.
 */
export function isDraftComplete(args: DraftCompletenessArgs): boolean {
  return draftGapsEmpty(draftGaps(args));
}

/**
 * Register-now path. Documents are not an input: this gate cannot be
 * satisfied by skipping the QuickBooks trio on the standard Submit button.
 * Opening must be the explicit pending choice, and the acknowledgement
 * must name a person. Nothing here is pre-selected.
 */
export function deferredRegistrationReady(input: {
  formReady: boolean;
  hasDraft: boolean;
  booksStart: string;
  openingChoice: "" | "pending" | "booked";
  acknowledged: boolean;
  acknowledgedBy: string;
}): boolean {
  if (!input.formReady || !input.hasDraft) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.booksStart)) return false;
  if (input.openingChoice !== "pending") return false;
  if (!input.acknowledged) return false;
  if (input.acknowledgedBy.trim() === "") return false;
  return true;
}

export function createSessionDraftStore(
  storage: Storage = sessionStorage,
): DraftStore {
  const read = (): PersistedDraftInventoryEntry[] => {
    try {
      const raw = storage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedEntry);
    } catch {
      return [];
    }
  };
  const write = (entries: PersistedDraftInventoryEntry[]): void => {
    storage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(entries));
  };
  return {
    list: read,
    upsert(entry) {
      const rest = read().filter((e) => e.draft.slug !== entry.draft.slug);
      write([...rest, entry]);
    },
    remove(slug) {
      write(read().filter((e) => e.draft.slug !== slug));
    },
  };
}

function isPersistedEntry(value: unknown): value is PersistedDraftInventoryEntry {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  const draft = record.draft;
  if (typeof draft !== "object" || draft === null) return false;
  const draftRec = draft as Record<string, unknown>;
  return (
    typeof draftRec.slug === "string" &&
    Array.isArray(record.documents) &&
    Array.isArray(record.optionalAcks) &&
    Array.isArray(record.decisions)
  );
}

const DETERMINATION_DRAFT_ROLES = [
  "determination-am-source",
  "determination-ocr-text",
  "determination-no-source-note",
  "determination-correct-value",
  "determination-tax-year",
  "determination-owner-attest",
] as const;

interface DeterminationFieldDraft {
  field: string;
  focusedRole: string | null;
  values: Record<string, string>;
  checked: Record<string, boolean>;
}

function snapshotDeterminationDrafts(host: HTMLElement): DeterminationFieldDraft[] {
  const drafts: DeterminationFieldDraft[] = [];
  const active = document.activeElement;
  for (const card of host.querySelectorAll<HTMLElement>(
    '[data-role="determination-card"]',
  )) {
    const field = card.getAttribute("data-field");
    if (field === null || field.length === 0) continue;
    const values: Record<string, string> = {};
    const checked: Record<string, boolean> = {};
    let focusedRole: string | null = null;
    for (const role of DETERMINATION_DRAFT_ROLES) {
      const el = card.querySelector(`[data-role="${role}"]`);
      if (el === null) continue;
      if (active !== null && (el === active || el.contains(active))) {
        focusedRole = role;
      }
      if (el instanceof HTMLInputElement && el.type === "checkbox") {
        checked[role] = el.checked;
      } else if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement
      ) {
        values[role] = el.value;
      }
    }
    drafts.push({ field, focusedRole, values, checked });
  }
  return drafts;
}

function restoreDeterminationDrafts(
  host: HTMLElement,
  drafts: readonly DeterminationFieldDraft[],
): void {
  for (const draft of drafts) {
    let card: HTMLElement | null = null;
    for (const candidate of host.querySelectorAll<HTMLElement>(
      '[data-role="determination-card"]',
    )) {
      if (candidate.getAttribute("data-field") === draft.field) {
        card = candidate;
        break;
      }
    }
    if (card === null) continue;
    for (const [role, value] of Object.entries(draft.values)) {
      const el = card.querySelector(`[data-role="${role}"]`);
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement
      ) {
        el.value = value;
      }
    }
    for (const [role, isChecked] of Object.entries(draft.checked)) {
      const el = card.querySelector(`[data-role="${role}"]`);
      if (el instanceof HTMLInputElement && el.type === "checkbox") {
        el.checked = isChecked;
      }
    }
    if (draft.focusedRole !== null) {
      const el = card.querySelector<HTMLElement>(
        `[data-role="${draft.focusedRole}"]`,
      );
      el?.focus();
    }
  }
}

export function renderOnboardingPage(opts: OnboardingPageOptions): void {
  const { client, root } = opts;
  const role = readerRole(opts.role);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const writePage = ++pageWriteSeq;
  const writeKey = (action: string): string => `${action}:${String(writePage)}`;
  /**
   * Tell the shell a company registered. The registration already succeeded by
   * the time this runs, so a failure here must never become an error banner on
   * a one-chance screen (round 2 review) — it is reported under the ok banner
   * as "the company list was not refreshed" (round 3 review).
   *
   * The call itself is wrapped in try/catch as well as the returned promise:
   * `Promise.resolve(...)` is only reached once the callback has returned, so a
   * synchronous throw would escape it. `reloadCompanies` is async (R8c), so both
   * shapes are real. Resolves true when the shell refresh ran.
   */
  const notifyRegistered = (slug: CompanySlug): Promise<boolean> => {
    let called: void | Promise<void>;
    try {
      called = opts.onRegistered?.(slug);
    } catch (error) {
      console.error("onRegistered threw after registration", error);
      return Promise.resolve(false);
    }
    return Promise.resolve(called).then(
      () => true,
      (error: unknown) => {
        console.error("onRegistered rejected after registration", error);
        return false;
      },
    );
  };
  /** Said under a successful registration banner when the shell did not refresh. */
  const REGISTERED_NOT_REFRESHED =
    "Company list not refreshed — reload the page.";
  const uploadRouteAvailable =
    opts.openingDocumentUploadRouteAvailable ??
    OPENING_DOCUMENT_UPLOAD_ROUTE_AVAILABLE;
  const draftStore = opts.draftStore ?? createSessionDraftStore();
  let serverDrafts: CompanyDraft[] = [];
  let serverDraftsError: string | null = null;
  let serverDraftsLoaded = false;
  const extractBySha = new Map<string, OpeningDocumentExtractResponse>();
  const extractInFlight = new Set<string>();
  const extractFetchedAt = new Map<string, number>();
  const extractLoadErrors = new Set<string>();
  const EXTRACT_PENDING_REFETCH_MS = 5_000;
  clear(root);

  root.append(
    h("header", { class: "page-header" }, [
      h("h1", { class: "page-title", "data-role": "onboarding-title" }, [
        navLabel("onboarding"),
      ]),
      h("p", { class: "lede", "data-role": "onboarding-lede" }, [
        "Confirm each reading, add the opening documents, then submit once. Nothing is guessed.",
      ]),
    ]),
  );

  if (opts.openAddCompany !== undefined) {
    const addCompany = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "open-add-company",
      },
      [navLabel("add-company")],
    );
    addCompany.addEventListener("click", () => {
      opts.openAddCompany?.();
    });
    root.append(
      h("p", { "data-role": "onboarding-add-company" }, [
        "New to the guided setup? ",
        addCompany,
      ]),
    );
  }

  const banner = h("div", {
    class: "banner",
    role: "status",
    "data-role": "onboarding-banner",
    hidden: "true",
  });
  root.append(banner);

  const accrualDialogTitle = h(
    "h2",
    {
      class: "page-title",
      id: "accrual-not-implemented-title",
      "data-role": "accrual-not-implemented-title",
    },
    [ACCRUAL_NOT_IMPLEMENTED_TITLE],
  );
  const accrualWarningCorrectBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-secondary",
      "data-action": "accrual-warning-correct",
      "aria-pressed": "false",
    },
    ["This is cash — go back and correct"],
  );
  const accrualWarningArmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-secondary",
      "data-action": "accrual-warning-arm",
      "aria-pressed": "false",
    },
    ["Record the determination — books stay cash"],
  );
  const accrualWarningConfirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "accrual-warning-confirm",
      hidden: "true",
    },
    ["Confirm — books stay cash until accrual is built"],
  );
  const accrualDialog = h(
    "div",
    {
      class: "banner banner-err",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "accrual-not-implemented-title",
      "data-role": "accrual-not-implemented-dialog",
      hidden: "true",
    },
    [
      accrualDialogTitle,
      h("p", { "data-role": "accrual-not-implemented-copy" }, [
        ACCRUAL_NOT_IMPLEMENTED_COPY,
      ]),
      h(
        "div",
        { class: "actions", "data-role": "accrual-not-implemented-actions" },
        [
          accrualWarningCorrectBtn,
          accrualWarningArmBtn,
          accrualWarningConfirmBtn,
        ],
      ),
    ],
  );
  accrualDialog.style.cssText =
    "display:none;position:relative;z-index:20;margin:0 0 16px;";
  root.append(accrualDialog);

  const state: FormState = {
    slug: "",
    displayName: "",
    companyType: "",
    basis: "",
    featurePacks: [],
    hubLink: "",
    hubLinkStated: false,
  };
  let session: DraftSession | null = null;
  /**
   * The one company directory this page reads (owner decision 14). Every
   * company word comes from `face.company`; a name the page has already read is
   * remembered here, and an unnamed draft or company reads "this company"
   * rather than its slug.
   */
  const knownCompanyNames = new Map<CompanySlug, string>();
  const rememberCompany = (row: {
    slug: CompanySlug;
    displayName: string | null;
  }): void => {
    const name = row.displayName?.trim() ?? "";
    // A "name" that is exactly the internal key is the slug wearing a name's
    // clothes (owner decision 14: the slug is never shown). A staff-entered
    // name that merely shares the spelling with a capital letter ("Acme") is
    // still a name; only the exact key is refused.
    if (name !== "" && name !== row.slug) knownCompanyNames.set(row.slug, name);
  };
  const companyDirectory = (): CompanyDirectory =>
    createCompanyDirectory(
      [...knownCompanyNames].map(([slug, displayName]) => ({ slug, displayName })),
      { complete: companiesRead === "loaded" },
    );
  /** Kind the AM picked for the upload, kept across a repaint (R6-21). */
  let chosenUploadKind: OpeningDocumentKind | "" = "";
  /** Abandon confirm arm — first click arms; second confirms. */
  let abandonArmedSlug: string | null = null;
  /** Fields with an unanswered determination PUT (one press, one write). */
  const determinationsInFlight = new Set<string>();
  let pendingAccrualCompleting: {
    det: DerivedDetermination;
    decision: DeterminationDecisionKind;
    correctedValue: string | undefined;
    sourceUsed: string;
    meta?: DeterminationDecideMeta;
  } | null = null;
  let commitDeterminationDecision: (
    det: DerivedDetermination,
    decision: DeterminationDecisionKind,
    correctedValue: string | undefined,
    sourceUsed: string,
    meta: DeterminationDecideMeta | undefined,
    opts?: { accrualWarningAcknowledged?: boolean },
  ) => Promise<void> = () => {
    return Promise.reject(new Error("determination recorder is not ready"));
  };

  const hideAccrualWarningDialog = (): void => {
    accrualDialog.hidden = true;
    accrualDialog.setAttribute("hidden", "true");
    accrualDialog.style.display = "none";
    accrualWarningConfirmBtn.hidden = true;
    accrualWarningConfirmBtn.setAttribute("hidden", "true");
    accrualWarningArmBtn.setAttribute("aria-pressed", "false");
  };

  const showAccrualWarningDialog = (): void => {
    accrualWarningConfirmBtn.hidden = true;
    accrualWarningConfirmBtn.setAttribute("hidden", "true");
    accrualWarningArmBtn.setAttribute("aria-pressed", "false");
    accrualDialog.hidden = false;
    accrualDialog.removeAttribute("hidden");
    accrualDialog.style.display = "block";
  };

  // --- Saved drafts (browser inventory + server GET) -----------------------
  const draftsHost = h("section", {
    class: "onboarding-drafts card",
    "data-role": "onboarding-draft-list",
  });
  root.append(draftsHost);

  const companyListHost = h("section", {
    class: "onboarding-companies card",
    "data-role": "onboarding-company-list",
  });
  root.append(companyListHost);

  // --- Company form (no pre-selected determinations) -----------------------
  const slugInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": face.ariaFor("Web address"),
    "data-role": "onboarding-slug",
    placeholder: "syn-demo-co",
    autocomplete: "off",
  });
  const nameInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": "Display name",
    "data-role": "onboarding-display-name",
    placeholder: "Synthetic Demo Co",
    autocomplete: "off",
  });

  // Radios with NOTHING checked — anchoring: never pre-select.
  const typeGroup = h("fieldset", {
    class: "onboarding-fieldset",
    "data-role": "onboarding-company-type",
  }, [h("legend", {}, ["Company type (required — no default)"])]);
  for (const value of COMPANY_TYPES) {
    const id = `onboarding-type-${value}`;
    const radio = h("input", {
      type: "radio",
      name: "onboarding-company-type",
      value,
      id,
      "data-role": `onboarding-type-${value}`,
    });
    // Explicitly ensure unchecked — never selected / checked attribute.
    radio.checked = false;
    radio.removeAttribute("checked");
    radio.addEventListener("change", () => {
      if (radio.checked) {
        state.companyType = value;
        // Switching away from medical clears a stated hub link requirement.
        if (value !== "gvpm-practice") {
          hubInput.value = "";
          state.hubLink = "";
          state.hubLinkStated = true; // null is the stated value for non-medical
        } else {
          state.hubLinkStated = false;
        }
        paint();
      }
    });
    typeGroup.append(
      h("label", { class: "choice-label", for: id }, [radio, ` ${faceValue(value)}`]),
    );
  }

  const basisGroup = h("fieldset", {
    class: "onboarding-fieldset",
    "data-role": "onboarding-basis",
  }, [
    h("legend", {}, ["Books basis (required — no default)"]),
    h("div", { class: "muted", "data-role": "onboarding-basis-sync-note" }, [
      "This is the basis saved with the company. Cash from the documents still updates it. Accrual is recorded as what the document says, and the books stay on cash until that close is built.",
      showDetails(
        "Choosing cash here updates the company's setting straight away. Choosing accrual is recorded here, and the books keep counting money when it moves until accrual bookkeeping is built.",
      ),
    ]),
  ]);
  for (const value of BASES) {
    const id = `onboarding-basis-${value}`;
    const radio = h("input", {
      type: "radio",
      name: "onboarding-basis",
      value,
      id,
      "data-role": `onboarding-basis-${value}`,
    });
    radio.checked = false;
    radio.removeAttribute("checked");
    radio.addEventListener("change", () => {
      if (radio.checked) {
        state.basis = value;
        // Form moved away from a completing books_basis disposition → clear
        // that disposition so Correct cannot silently disagree with promote.
        if (session !== null) {
          const booksDec = session.decisions.find(
            (d) =>
              d.field === "books_basis" &&
              isVerifiedCompletingDecision(d),
          );
          if (booksDec) {
            const proposal = currentProposals().find(
              (p) => p.field === "books_basis",
            );
            const resolved = resolvedBooksBasisValue(booksDec, proposal);
            if (resolved !== null && resolved !== value) {
              session = {
                ...session,
                decisions: session.decisions.filter(
                  (d) => d.field !== "books_basis",
                ),
              };
              persistSession();
              // The green "Recorded Books basis: Cash." banner above this form
              // describes a reading this handler just dropped. Leaving it up
              // makes the page claim a reading it no longer holds (round 4
              // review). Name the field and the value the form now carries.
              showBanner(
                "err",
                `${faceField("books_basis")} on the form changed to ${faceValue(value)}, so the saved ${faceField("books_basis")} reading was cleared. Record it again.`,
              );
            } else if (session.draft.basis !== value) {
              session = {
                ...session,
                draft: { ...session.draft, basis: value },
              };
              persistSession();
            }
          } else if (session.draft.basis !== value) {
            session = {
              ...session,
              draft: { ...session.draft, basis: value },
            };
            persistSession();
          }
        }
        paint();
      }
    });
    basisGroup.append(
      h("label", { class: "choice-label", for: id }, [radio, ` ${faceValue(value)}`]),
    );
  }

  const packsGroup = h("fieldset", {
    class: "onboarding-fieldset",
    "data-role": "onboarding-feature-packs",
  }, [h("legend", {}, ["Feature packs (required — none pre-checked)"])]);
  for (const value of PACKS) {
    const id = `onboarding-pack-${value}`;
    const box = h("input", {
      type: "checkbox",
      name: "onboarding-feature-packs",
      value,
      id,
      "data-role": `onboarding-pack-${value}`,
    });
    box.checked = false;
    box.removeAttribute("checked");
    box.addEventListener("change", () => {
      state.featurePacks = PACKS.filter((p) => {
        const el = packsGroup.querySelector<HTMLInputElement>(
          `[data-role="onboarding-pack-${p}"]`,
        );
        return el?.checked === true;
      });
      paint();
    });
    packsGroup.append(
      h("label", { class: "choice-label", for: id }, [box, ` ${faceValue(value)}`]),
    );
  }

  const hubInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": face.ariaFor("Practice connection id"),
    "data-role": "onboarding-hub-link",
    placeholder: "Practice id from the hub",
    autocomplete: "off",
  });
  hubInput.addEventListener("input", () => {
    state.hubLink = hubInput.value;
    state.hubLinkStated = true;
    paint();
  });

  slugInput.addEventListener("input", () => {
    state.slug = slugInput.value.trim().toLowerCase();
    paint();
  });
  nameInput.addEventListener("input", () => {
    state.displayName = nameInput.value;
    paint();
  });

  const saveDraftBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "save-draft",
      "aria-label": "Save company draft",
    },
    ["Save draft"],
  );

  const form = h("section", { class: "onboarding-form card", "data-role": "onboarding-form" }, [
    h("h2", { class: "section-title" }, ["Company"]),
    h("label", { class: "file-label field" }, [
      h("span", { class: "field-label" }, ["Web address"]),
      slugInput,
    ]),
    h("label", { class: "file-label field" }, [
      h("span", { class: "field-label" }, ["Display name"]),
      nameInput,
    ]),
    typeGroup,
    basisGroup,
    packsGroup,
    h("label", { class: "file-label field" }, [
      h("span", { class: "field-label" }, ["Practice connection id"]),
      hubInput,
    ]),
    h("div", { class: "muted", "data-role": "onboarding-hub-link-note" }, [
      "Saved with the company. This does not turn on the live connection. The owner sets that up after you submit.",
      showDetails(
        "Stored on the company row as hub_link. Does not create the operational Hub link (hub_linked_companies) — that remains a separate owner provisioning step after Submit.",
      ),
    ]),
    saveDraftBtn,
  ]);
  root.append(form);

  // --- Draft view: present AND missing ------------------------------------
  const draftHost = h("section", {
    class: "onboarding-draft card",
    "data-role": "onboarding-draft",
  });
  root.append(draftHost);

  // --- Document upload (blocked until signed-upload route exists) ---------
  const docsHost = h("section", {
    class: "onboarding-documents card",
    "data-role": "onboarding-documents",
  });
  root.append(docsHost);

  // --- Derived determinations (anchoring) ---------------------------------
  const detHost = h("section", {
    class: "onboarding-determinations card",
    "data-role": "onboarding-determinations",
  });
  root.append(detHost);

  // --- Submit -------------------------------------------------------------
  const submitReason = h("p", {
    class: "muted",
    role: "status",
    "data-role": "submit-reason",
  });
  const submitBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "submit-draft",
      "aria-label": "Submit company draft",
      disabled: "true",
    },
    ["Submit"],
  );
  const booksStartInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": "Books start date",
    "data-role": "onboarding-books-start",
    placeholder: "YYYY-MM-DD",
    autocomplete: "off",
  });
  const openingPending = h("input", {
    type: "radio",
    name: "onboarding-opening",
    value: "pending",
    id: "onboarding-opening-pending",
    "data-role": "onboarding-opening-pending",
  });
  const openingBooked = h("input", {
    type: "radio",
    name: "onboarding-opening",
    value: "booked",
    id: "onboarding-opening-booked",
    "data-role": "onboarding-opening-booked",
  });
  openingPending.checked = false;
  openingBooked.checked = false;
  openingPending.removeAttribute("checked");
  openingBooked.removeAttribute("checked");
  const deferredAckBox = h("input", {
    type: "checkbox",
    id: "onboarding-opening-deferred-ack",
    "data-role": "opening-deferred-ack",
  });
  deferredAckBox.checked = false;
  deferredAckBox.removeAttribute("checked");
  const deferredReason = h("p", {
    class: "muted",
    role: "status",
    "data-role": "deferred-reason",
  });
  const deferredAckRecord = h("p", {
    class: "muted",
    "data-role": "opening-deferred-ack-record",
  });
  const deferredBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "register-opening-later",
      "aria-label": "Register now — opening books later",
      disabled: "true",
    },
    ["Register now — opening books later"],
  );
  root.append(
    h("section", { class: "onboarding-submit card", "data-role": "onboarding-submit" }, [
      h("h2", { class: "section-title" }, ["Submit"]),
      submitReason,
      submitBtn,
      h("h2", { class: "section-title" }, ["Register now — opening books later"]),
      h("p", { class: "muted" }, [
        "This path does not use the QuickBooks reports. It stays disabled until books start is filled, opening is pending, and the acknowledgement is checked. Neither choice starts selected.",
      ]),
      h("label", { class: "file-label" }, ["Books start", booksStartInput]),
      h("fieldset", { class: "onboarding-fieldset", "data-role": "onboarding-opening" }, [
        h("legend", {}, ["Opening (required for this path — no default)"]),
        h("label", { class: "choice-label", for: "onboarding-opening-pending" }, [
          openingPending,
          " pending",
        ]),
        h("label", { class: "choice-label", for: "onboarding-opening-booked" }, [
          openingBooked,
          " booked",
        ]),
      ]),
      h("label", { class: "choice-label", for: "onboarding-opening-deferred-ack" }, [
        deferredAckBox,
        " Opening books deferred",
      ]),
      deferredAckRecord,
      deferredReason,
      deferredBtn,
    ]),
  );

  const showBanner = (kind: "ok" | "err", message: string, detail?: string): void => {
    clear(banner);
    banner.hidden = false;
    banner.removeAttribute("hidden");
    banner.className = kind === "ok" ? "banner banner-ok" : "banner banner-err";
    banner.append(document.createTextNode(message));
    if (detail !== undefined && detail !== "") banner.append(showDetails(detail));
  };

  /**
   * The ok banner for a completed registration, plus the appender for the
   * shell-refresh line. The company is already registered when this runs, so
   * the refresh must not be awaited before the banner or before `paint()`
   * (round 4 review): the screen says the company registered immediately and
   * the "list not refreshed" line arrives when the shell answers, or never.
   *
   * The appender edits only the text node this call created, and only while it
   * is still the banner's message. A later error or a repaint that replaced the
   * banner must not be rewritten after the fact.
   */
  const showRegisteredBanner = (
    message: string,
    detail?: string,
  ): ((refreshed: boolean) => void) => {
    showBanner("ok", message, detail);
    const messageNode = banner.firstChild;
    return (refreshed: boolean): void => {
      if (refreshed || !stillCurrent()) return;
      if (messageNode === null || messageNode.parentNode !== banner) return;
      if (messageNode.textContent?.includes(REGISTERED_NOT_REFRESHED)) return;
      messageNode.textContent = `${messageNode.textContent ?? ""} ${REGISTERED_NOT_REFRESHED}`;
    };
  };

  const taxReturnExtractView = (): OpeningDocumentExtractResponse | null => {
    const tax = (session?.documents ?? []).find(
      (d) => d.kind === "tax-return" && d.r2Key !== null,
    );
    if (tax === undefined) return null;
    return extractBySha.get(tax.contentSha256Hex) ?? null;
  };

  const currentProposals = (): DerivedDetermination[] => {
    const rows = produceDerivedDeterminations(
      session?.documents ?? [],
      opts.derivedDeterminations,
      taxReturnExtractView(),
    );
    const tax = (session?.documents ?? []).find(
      (d) => d.kind === "tax-return" && d.r2Key !== null,
    );
    if (tax !== undefined && extractLoadErrors.has(tax.contentSha256Hex)) {
      return rows.map((row) => ({ ...row, extractLoadError: true }));
    }
    return rows;
  };

  const ensureExtracts = (): void => {
    if (opts.derivedDeterminations !== undefined) return;
    if (session === null) return;
    if (typeof client.getOpeningDocumentExtract !== "function") return;
    const taxDocs = session.documents.filter(
      (d) => d.kind === "tax-return" && d.r2Key !== null,
    );
    const now = Date.now();
    for (const doc of taxDocs) {
      const sha = doc.contentSha256Hex;
      if (extractInFlight.has(sha)) continue;
      const cached = extractBySha.get(sha);
      if (cached?.status === "ready" || cached?.status === "not_extractable") {
        continue;
      }
      const fetchedAt = extractFetchedAt.get(sha) ?? 0;
      if (fetchedAt > 0 && now - fetchedAt < EXTRACT_PENDING_REFETCH_MS) {
        continue;
      }
      extractInFlight.add(sha);
      extractFetchedAt.set(sha, now);
      void client
        .getOpeningDocumentExtract(session.draft.slug, "tax-return", sha)
        .then((view) => {
          const prev = extractBySha.get(sha);
          extractBySha.set(sha, view);
          extractLoadErrors.delete(sha);
          extractInFlight.delete(sha);
          const changed =
            prev === undefined ||
            prev.status !== view.status ||
            JSON.stringify(prev.extract) !== JSON.stringify(view.extract);
          if (stillCurrent() && changed) paint();
          if (
            (view.status === "pending" || view.status === "failed") &&
            stillCurrent()
          ) {
            window.setTimeout(() => {
              if (stillCurrent()) ensureExtracts();
            }, EXTRACT_PENDING_REFETCH_MS);
          }
        })
        .catch(() => {
          extractInFlight.delete(sha);
          const wasError = extractLoadErrors.has(sha);
          extractLoadErrors.add(sha);
          if (stillCurrent() && !wasError) paint();
          if (stillCurrent()) {
            window.setTimeout(() => {
              if (stillCurrent()) ensureExtracts();
            }, EXTRACT_PENDING_REFETCH_MS);
          }
        });
    }
  };

  const persistSession = (): void => {
    if (session === null) return;
    draftStore.upsert({
      draft: session.draft,
      documents: session.documents,
      optionalAcks: session.optionalAcks,
      decisions: session.decisions,
    });
  };

  const refreshServerDrafts = async (): Promise<void> => {
    if (typeof client.listCompanyDrafts !== "function") {
      serverDraftsLoaded = true;
      return;
    }
    try {
      const body = await client.listCompanyDrafts();
      serverDrafts = body.drafts;
      serverDraftsError = null;
    } catch {
      serverDraftsError = "Could not load drafts from the server.";
    }
    serverDraftsLoaded = true;
  };

  const refreshCompanies = async (): Promise<void> => {
    if (typeof client.listCompanies !== "function") {
      companiesRead = "loaded";
      return;
    }
    try {
      const body = await client.listCompanies();
      listedCompanies = body.companies;
      for (const row of body.companies) rememberCompany(row);
      companiesRead = "loaded";
    } catch {
      // A failed read is not an empty list: keep what was listed before and
      // say it failed (round eight B5).
      companiesRead = "failed";
    }
  };

  const applyDraftToForm = (draft: CompanyDraft): void => {
    rememberCompany(draft);
    state.slug = draft.slug;
    state.displayName = draft.displayName;
    state.companyType = draft.companyType;
    state.basis = draft.basis;
    state.featurePacks = [...draft.featurePacks];
    state.hubLink = draft.hubLink ?? "";
    state.hubLinkStated = true;
    slugInput.value = draft.slug;
    nameInput.value = draft.displayName;
    hubInput.value = draft.hubLink ?? "";
    for (const value of COMPANY_TYPES) {
      const el = typeGroup.querySelector<HTMLInputElement>(
        `[data-role="onboarding-type-${value}"]`,
      );
      if (el) el.checked = value === draft.companyType;
    }
    for (const value of BASES) {
      const el = basisGroup.querySelector<HTMLInputElement>(
        `[data-role="onboarding-basis-${value}"]`,
      );
      if (el) el.checked = value === draft.basis;
    }
    for (const value of PACKS) {
      const el = packsGroup.querySelector<HTMLInputElement>(
        `[data-role="onboarding-pack-${value}"]`,
      );
      if (el) el.checked = draft.featurePacks.includes(value);
    }
  };

  const paintDraftList = (): void => {
    clear(draftsHost);
    draftsHost.append(h("h2", { class: "section-title" }, ["Saved drafts"]));
    draftsHost.append(
      h("div", { class: "muted", "data-role": "draft-list-flag" }, [
        "Drafts here are the ones in this browser and the ones already saved. Abandon removes only the copy in this browser.",
        showDetails(
          "The saved draft keeps this web id. There is no way to delete a saved draft yet.",
        ),
      ]),
    );
    if (serverDraftsError !== null) {
      draftsHost.append(
        h("p", { class: "muted", "data-role": "draft-list-server-error" }, [
          serverDraftsError,
        ]),
      );
    } else if (!serverDraftsLoaded) {
      draftsHost.append(
        h("p", { class: "muted", "data-role": "draft-list-server-loading" }, [
          "Loading server drafts…",
        ]),
      );
    }
    const localEntries = draftStore.list();
    for (const entry of localEntries) rememberCompany(entry.draft);
    for (const draft of serverDrafts) rememberCompany(draft);
    const localBySlug = new Map(localEntries.map((entry) => [entry.draft.slug, entry]));
    const merged: {
      entry: PersistedDraftInventoryEntry;
      origin: "local" | "server";
    }[] = localEntries.map((entry) => ({ entry, origin: "local" as const }));
    for (const draft of serverDrafts) {
      if (localBySlug.has(draft.slug)) continue;
      merged.push({
        origin: "server",
        entry: {
          draft,
          documents: [],
          optionalAcks: [],
          decisions: [],
        },
      });
    }
    if (merged.length === 0) {
      draftsHost.append(
        h("p", { class: "muted", "data-role": "draft-list-empty" }, [
          serverDraftsLoaded && serverDraftsError === null
            ? "No saved drafts yet. Fill in the company form below, then save a draft."
            : "No saved drafts in this browser yet. Fill in the company form below, then save a draft.",
        ]),
      );
      return;
    }
    const list = h("ul", { "data-role": "draft-list-items" });
    for (const row of merged) {
      const entry = row.entry;
      const slug = entry.draft.slug;
      const origin = row.origin;
      const openBtn = h(
        "button",
        {
          type: "button",
          class: "btn",
          "data-action": "open-draft",
          "data-slug": slug,
          "aria-label": face.ariaFor(
            `Open draft ${face.company(slug, companyDirectory(), "sentence")}`,
          ),
        },
        ["Open"],
      );
      openBtn.addEventListener("click", () => {
        session = {
          draft: entry.draft,
          documents: [...entry.documents],
          optionalAcks: [...entry.optionalAcks],
          decisions: [...entry.decisions],
        };
        abandonArmedSlug = null;
        applyDraftToForm(entry.draft);
        persistSession();
        const booksDec = session.decisions.find(
          (d) =>
            d.field === "books_basis" && isVerifiedCompletingDecision(d),
        );
        if (booksDec) {
          const proposal = produceDerivedDeterminations(
            session.documents,
            opts.derivedDeterminations,
          ).find((p) => p.field === "books_basis");
          const resolved = resolvedBooksBasisValue(booksDec, proposal);
          if (
            resolved !== null &&
            state.basis !== resolved &&
            (resolved !== "accrual" || operationalAccrualWritesAllowed())
          ) {
            syncFormBasis(resolved);
            persistSession();
          }
        }
        showBanner(
          "ok",
          `Opened the draft for ${face.company(slug, companyDirectory(), "sentence")}.`,
        );
        paint();
      });

      const abandonLabel =
        abandonArmedSlug === slug ? "Confirm abandon" : "Abandon";
      const abandonBtn =
        origin === "local"
          ? h(
              "button",
              {
                type: "button",
                class: "btn",
                "data-action":
                  abandonArmedSlug === slug
                    ? "confirm-abandon-draft"
                    : "abandon-draft",
                "data-slug": slug,
                "aria-label": face.ariaFor(
                  `${abandonLabel} draft ${face.company(slug, companyDirectory(), "sentence")}`,
                ),
              },
              [abandonLabel],
            )
          : null;
      if (abandonBtn) {
        abandonBtn.addEventListener("click", () => {
          if (abandonArmedSlug !== slug) {
            abandonArmedSlug = slug;
            paint();
            return;
          }
          draftStore.remove(slug);
          if (session?.draft.slug === slug) {
            session = null;
          }
          abandonArmedSlug = null;
          showBanner(
            "ok",
            `Abandoned the local draft for ${face.company(slug, companyDirectory(), "sentence")}.`,
            "The saved copy on the server may still be there until it can be deleted.",
          );
          paint();
        });
      }

      const cancelArm =
        origin === "local" && abandonArmedSlug === slug
          ? h(
              "button",
              {
                type: "button",
                class: "btn",
                "data-action": "cancel-abandon-draft",
                "data-slug": slug,
              },
              ["Cancel"],
            )
          : null;
      if (cancelArm) {
        cancelArm.addEventListener("click", () => {
          abandonArmedSlug = null;
          paint();
        });
      }

      list.append(
        h(
          "li",
          {
            "data-draft-slug": slug,
            "data-draft-origin": origin,
          },
          [
            h("span", { "data-role": "draft-list-slug" }, [
              face.company(slug, companyDirectory(), "sentence"),
            ]),
            origin === "server"
              ? h("span", { class: "muted", "data-role": "draft-list-origin" }, [
                  "saved on the server",
                ])
              : null,
            openBtn,
            abandonBtn,
            cancelArm,
          ],
        ),
      );
    }
    draftsHost.append(list);
  };

  const paintDraftView = (): void => {
    clear(draftHost);
    draftHost.append(h("h2", { class: "section-title" }, ["Draft status"]));

    const docs = session?.documents ?? [];
    const gaps = draftGaps({
      state,
      documents: docs,
      optionalAcks: session?.optionalAcks ?? [],
      proposals: currentProposals(),
      decisions: session?.decisions ?? [],
    });

    const presentList = h("ul", {
      class: "onboarding-present",
      "data-role": "draft-present",
    });
    const missingList = h("ul", {
      class: "onboarding-missing",
      "data-role": "draft-missing",
    });

    // Present determinations
    if (state.slug && SLUG_RE.test(state.slug)) {
      presentList.append(
        h("li", { "data-present": "slug" }, [
          `Web address: ${state.slug}`,
        ]),
      );
    }
    if (state.displayName.trim()) {
      // Untrusted — textContent via h()/createTextNode; line-hazards stripped.
      presentList.append(
        h("li", { "data-present": "displayName" }, [
          `Display name: ${amVisibleDisplayText(safeText(state.displayName.trim()))}`,
          showDetails(`displayName: ${amVisibleDisplayText(safeText(state.displayName.trim()))}`),
        ]),
      );
    }
    if (state.companyType !== "") {
      presentList.append(
        h("li", { "data-present": "companyType" }, [
          `Company type: ${faceValue(state.companyType)}.`,
          showDetails(`companyType: ${state.companyType}`),
        ]),
      );
    }
    if (state.basis !== "") {
      presentList.append(
        h("li", { "data-present": "basis" }, [
          `Books basis: ${faceValue(state.basis)}.`,
          showDetails(`basis: ${state.basis}`),
        ]),
      );
    }
    if (state.featurePacks.length > 0) {
      presentList.append(
        h("li", { "data-present": "featurePacks" }, [
          `Included: ${state.featurePacks.map((pack) => faceValue(pack)).join(", ")}.`,
          showDetails(`featurePacks: ${state.featurePacks.join(", ")}`),
        ]),
      );
    }
    if (state.companyType === "gvpm-practice" && state.hubLinkStated && state.hubLink.trim()) {
      presentList.append(
        h("li", { "data-present": "hubLink" }, [
          `Practice connection: ${safeText(state.hubLink.trim())}.`,
          showDetails(`hubLink: ${safeText(state.hubLink.trim())}`),
        ]),
      );
    } else if (state.companyType !== "" && state.companyType !== "gvpm-practice" && state.hubLinkStated) {
      presentList.append(
        h("li", { "data-present": "hubLink" }, [
          "Practice connection: None.",
          showDetails("hubLink: null"),
        ]),
      );
    }

    for (const doc of docs.filter((d) => d.r2Key !== null)) {
      presentList.append(
        h("li", { "data-present": `doc-${doc.kind}`, "data-doc-kind": doc.kind }, [
          // Filename is untrusted — text node only; never render GL bytes.
          `${faceDoc(doc.kind)}: ${amVisibleDisplayText(safeText(doc.filename))}`,
          showDetails(`${doc.kind}: ${amVisibleDisplayText(safeText(doc.filename))}`),
          doc.quarantine
            ? h("span", { class: "muted", "data-role": "quarantine-note" }, [
                " (The full ledger detail is stored separately and is not shown.)",
                showDetails("GL detail quarantined in R2 — not rendered"),
              ])
            : null,
        ]),
      );
    }
    for (const ack of session?.optionalAcks ?? []) {
      presentList.append(
        h("li", { "data-present": `ack-${ack.kind}`, "data-ack-kind": ack.kind }, [
          `${faceDoc(ack.kind)} was confirmed missing.`,
          showDetails(
            `optional ${ack.kind} acknowledged missing by ${amVisibleDisplayText(safeText(ack.acknowledgedBy))} at ${ack.acknowledgedAt}`,
          ),
        ]),
      );
    }

    for (const key of gaps.determinations) {
      missingList.append(
        h("li", { "data-missing": key }, [
          `${faceField(key)} is still needed.`,
          showDetails(`Missing determination: ${key}`),
        ]),
      );
    }
    for (const kind of gaps.documents) {
      missingList.append(
        h("li", { "data-missing": `doc-${kind}`, "data-doc-kind": kind }, [
          `${faceDoc(kind)} is still needed.`,
          showDetails(`Missing required document: ${kind}`),
        ]),
      );
    }
    for (const kind of gaps.optionalDocuments) {
      missingList.append(
        h("li", {
          "data-missing": `optional-doc-${kind}`,
          "data-doc-kind": kind,
        }, [
          `${faceDoc(kind)} is optional. Confirm it is missing to continue.`,
          showDetails(`Optional document missing (acknowledge to proceed): ${kind}`),
        ]),
      );
    }
    for (const field of gaps.undisposedProposals) {
      missingList.append(
        h("li", {
          "data-missing": `proposal-${field}`,
          "data-proposal-field": field,
        }, [
          `${faceField(field)} still needs a decision.`,
          showDetails(`Undisposed determination: ${safeText(field)}`),
        ]),
      );
    }
    for (const field of gaps.refusedProposals) {
      missingList.append(
        h("li", {
          "data-missing": `refused-${field}`,
          "data-proposal-field": field,
          "data-refused": "true",
        }, [
          `${faceField(field)} was refused. Correct it before you can submit.`,
          showDetails(`Refused determination — Correct required: ${safeText(field)}`),
        ]),
      );
    }
    for (const field of gaps.noSourceProposals) {
      missingList.append(
        h("li", {
          "data-missing": `no-source-${field}`,
          "data-proposal-field": field,
          "data-no-source": "true",
        }, [
          `${faceField(field)} has no source yet. Paste the line from the document.`,
          showDetails(`No source found — Correct with a quotation required: ${safeText(field)}`),
        ]),
      );
    }
    for (const detail of gaps.basisDisagreements) {
      missingList.append(
        h("li", {
          "data-missing": "basis-disagreement",
          "data-role": "basis-disagreement",
        }, [`Basis disagreement: ${safeText(detail)}`]),
      );
    }

    if (presentList.childNodes.length === 0) {
      presentList.append(
        h("li", { class: "muted", "data-role": "draft-present-empty" }, [
          "Nothing present yet. Choose the company type and basis, then save a draft.",
        ]),
      );
    }
    if (missingList.childNodes.length === 0) {
      missingList.append(
        h("li", { class: "muted", "data-role": "draft-missing-empty" }, [
          "Nothing missing — ready to submit.",
        ]),
      );
    }

    draftHost.append(
      h("h3", { class: "section-title" }, ["Present"]),
      presentList,
      h("h3", { class: "section-title" }, ["Missing"]),
      missingList,
    );

    if (session?.draft) {
      draftHost.append(
        h("p", { class: "muted", "data-role": "draft-slug" }, [
          `Draft for this company · created by ${amVisibleDisplayText(safeText(session.draft.createdBy))}`,
        ]),
      );
    }
  };

  const paintDocuments = (): void => {
    clear(docsHost);
    docsHost.append(h("h2", { class: "section-title" }, ["Opening documents"]));
    if (!uploadRouteAvailable) {
      docsHost.append(
        h("p", {
          class: "banner banner-err",
          role: "status",
          "data-role": "upload-route-flag",
        }, [
          "Document upload is blocked: the upload route is not available yet. This page will not proxy bytes or invent a route. Required kinds below stay listed as missing until that route lands.",
        ]),
      );
    }

    const kindSelect = h("select", {
      "aria-label": "Document kind",
      "data-role": "onboarding-doc-kind",
      ...(uploadRouteAvailable ? {} : { disabled: "true" }),
    });
    // Leading empty option — never pre-select a kind.
    kindSelect.append(h("option", { value: "" }, ["Choose a document…"]));
    for (const kind of OPENING_DOCUMENT_KINDS) {
      kindSelect.append(h("option", { value: kind }, [faceDoc(kind)]));
    }
    const fileInput = h("input", {
      type: "file",
      "aria-label": "Opening document file",
      "data-role": "onboarding-doc-file",
      ...(uploadRouteAvailable ? {} : { disabled: "true" }),
    });
    // UX hint only — mint refuses the wrong extension (R22A-2). Never
    // pre-select a kind, so accept stays unset until the AM picks one.
    kindSelect.addEventListener("change", () => {
      const kind = kindSelect.value;
      chosenUploadKind = isOpeningDocumentKind(kind) ? kind : "";
      const accept = acceptForOpeningDocumentKind(
        kind === "" ? "" : (kind as OpeningDocumentKind),
      );
      if (accept === "") fileInput.removeAttribute("accept");
      else fileInput.setAttribute("accept", accept);
    });
    if (chosenUploadKind !== "") {
      // A repaint (another action's banner, a draft refresh) must not silently
      // clear the kind the AM already chose mid-upload (R6-21, LOW).
      kindSelect.value = chosenUploadKind;
      const accept = acceptForOpeningDocumentKind(chosenUploadKind);
      if (accept !== "") fileInput.setAttribute("accept", accept);
    }
    const uploadBtn = h(
      "button",
      {
        type: "button",
        class: "btn",
        "data-action": "upload-opening-doc",
        "aria-label": "Upload opening document",
        ...(uploadRouteAvailable && session !== null
          ? {}
          : { disabled: "true" }),
      },
      ["Upload document"],
    );
    if (uploadRouteAvailable) {
      uploadBtn.addEventListener("click", () => {
        void (async () => {
          if (!stillCurrent() || session === null) return;
          const kindRaw = kindSelect.value;
          const file = fileInput.files?.[0];
          if (!isOpeningDocumentKind(kindRaw)) {
            showBanner("err", "Select a document kind before uploading.");
            return;
          }
          const kind = kindRaw;
          if (!(file instanceof File) || file.size <= 0) {
            showBanner("err", "Choose a file before uploading.");
            return;
          }
          const live = session;
          let stored: OpeningDocumentUploadResponse | undefined;
          const outcome = await runWrite({
            key: writeKey(`onboarding-upload:${kind}`),
            scope: docsHost,
            gate: () => null,
            send: async () => {
              const contentSha256Hex = await sha256HexOfBlob(file);
              const minted = await client.mintOpeningDocumentUploadUrl(
                live.draft.slug,
                {
                  kind,
                  contentSha256Hex,
                  filename: file.name,
                },
              );
              stored = await client.putOpeningDocumentUpload(minted.url, file);
              return stored;
            },
            stillCurrent,
          });
          if (!stillCurrent()) return;
          const uploadError = outcome.kind === "landed" ? undefined : outcome.error;
          if (outcome.kind !== "landed" || stored === undefined) {
            const failure = humanUploadFailure(uploadError, role);
            showBanner("err", failure.sentence, failure.detail ?? undefined);
            paint();
            return;
          }
          const row: DraftDocumentRow = {
            kind,
            filename: file.name,
            contentSha256Hex: stored.contentSha256Hex,
            r2Key: stored.objectKey,
            quarantine: isQuarantineKind(kind),
            uploadedAt: stored.uploadedAt,
          };
          // One row per kind, except the kinds a client legitimately has
          // several of: a second statement or loan document is added, not
          // swapped in, so earlier files stay visible (R6-06).
          const multiFile = MULTI_FILE_DOCUMENT_KINDS.includes(kind);
          const earlier = live.documents.filter((d) => d.kind === kind);
          // The very same bytes under the same kind is the same document, so
          // it cannot invalidate a reading taken from it (round 3 review).
          const identicalReupload = earlier.some(
            (d) => d.contentSha256Hex === row.contentSha256Hex,
          );
          const clearsReadings = !multiFile && !identicalReupload;
          const clearedFields = new Set(fieldsTiedToDocumentKind(kind));
          const decisionsNow = live.decisions;
          // Name only a reading this upload actually drops. A first upload has
          // no decision to clear, a multi-file kind keeps its decisions, and an
          // identical re-upload keeps them too, so the banner must stay silent
          // in all three cases (round 2 and round 3 reviews).
          const clearedNames = clearsReadings
            ? [...clearedFields]
                .filter((field) =>
                  decisionsNow.some((d) => d.field === field),
                )
                .map((field) => faceField(field))
            : [];
          const documents = multiFile
            ? [
                // Same object under a new name replaces its own row only:
                // the fingerprint is compared inside this kind (round 2
                // review), because two kinds can legitimately share bytes.
                ...live.documents.filter(
                  (d) =>
                    !(
                      d.kind === kind &&
                      d.contentSha256Hex === row.contentSha256Hex
                    ),
                ),
                row,
              ]
            : [...live.documents.filter((d) => d.kind !== kind), row];
          const decisions = clearsReadings
            ? live.decisions.filter((d) => !clearedFields.has(d.field))
            : live.decisions;
          // Uploading an optional kind clears any "missing" ack for it.
          const optionalAcks = live.optionalAcks.filter((a) => a.kind !== kind);
          session = {
            draft: live.draft,
            documents,
            optionalAcks,
            decisions,
          };
          persistSession();
          const kept = multiFile
            ? earlier.length > 0
              ? ` Earlier files are kept.`
              : ""
            : identicalReupload
              ? ` This is the same file already on this card.`
              : earlier.length > 0
                ? ` This replaced the earlier file.`
                : "";
          const cleared =
            clearedNames.length > 0
              ? ` It cleared the saved reading for ${clearedNames.join(", ")}. Record it again.`
              : "";
          showBanner(
            "ok",
            `Uploaded ${safeText(file.name)} as ${faceDoc(kind)}.${kept}${cleared}`,
            "The file was checked against its fingerprint at upload; intake still refuses a mismatch.",
          );
          chosenUploadKind = "";
          kindSelect.value = "";
          fileInput.value = "";
          paint();
        })();
      });
    }
    docsHost.append(
      h("div", { class: "statements-upload", "data-role": "onboarding-upload" }, [
        h("label", { class: "file-label" }, ["Kind", kindSelect]),
        h("label", { class: "file-label" }, ["File", fileInput]),
        uploadBtn,
      ]),
    );

    if (uploadRouteAvailable && session === null) {
      docsHost.append(
        h("p", { class: "muted", "data-role": "upload-draft-hint" }, [
          "Save a draft first. The file is stored with that company.",
        ]),
      );
    }

    const inventory = h("ul", { "data-role": "uploaded-doc-list" });
    for (const doc of session?.documents ?? []) {
      if (doc.r2Key === null) continue;
      inventory.append(
        h("li", { "data-uploaded-kind": doc.kind }, [
          `${faceDoc(doc.kind)}: ${amVisibleDisplayText(safeText(doc.filename))}`,
          showDetails(`${doc.kind}: ${amVisibleDisplayText(safeText(doc.filename))}`),
          doc.quarantine
            ? h("span", { class: "muted", "data-role": "quarantine-note" }, [
                " (The full ledger detail is stored separately and is not shown.)",
                showDetails("GL detail quarantined in R2 — not rendered"),
              ])
            : null,
        ]),
      );
    }
    if (inventory.childNodes.length > 0) {
      docsHost.append(
        h("p", { class: "muted" }, ["Uploaded (metadata only — no GL render):"]),
        inventory,
      );
    }

    const required = h("ul", { "data-role": "required-doc-kinds" });
    for (const kind of OPENING_REQUIRED_DOCUMENT_KINDS) {
      required.append(h("li", { "data-required-kind": kind }, [faceDoc(kind)]));
    }
    docsHost.append(
      h("p", { class: "muted" }, ["Required document kinds (block Submit):"]),
      required,
    );

    const optional = h("ul", { "data-role": "optional-doc-kinds" });
    for (const kind of OPENING_OPTIONAL_DOCUMENT_KINDS) {
      optional.append(
        h("li", { "data-optional-kind": kind }, [
          faceDoc(kind),
          showDetails(kind),
        ]),
      );
    }
    docsHost.append(
      h("p", { class: "muted" }, [
        "Optional document kinds (acknowledge to proceed without):",
      ]),
      optional,
    );

    // Optional-doc acknowledgement controls
    const missingOptional = missingOptionalDocumentKinds(
      session?.documents ?? [],
      session?.optionalAcks ?? [],
    );
    if (session !== null && missingOptional.length > 0) {
      const ackHost = h("div", {
        class: "onboarding-optional-acks",
        "data-role": "optional-doc-acks",
      });
      for (const kind of missingOptional) {
        const noteInput = h("input", {
          type: "text",
          class: "input",
          "aria-label": face.ariaFor(`Acknowledgement note for missing ${faceDoc(kind)}`),
          "data-role": `optional-ack-note-${kind}`,
          placeholder: "Why this optional document is unavailable",
        });
        const ackBtn = h(
          "button",
          {
            type: "button",
            class: "btn",
            "data-action": "acknowledge-optional-doc",
            "data-kind": kind,
            "aria-label": face.ariaFor(`Acknowledge missing ${faceDoc(kind)}`),
          },
          [`Acknowledge missing ${faceDoc(kind)}`],
        );
        ackBtn.addEventListener("click", () => {
          if (session === null) return;
          const note = noteInput.value.trim();
          if (!note) {
            showBanner(
              "err",
              `Add a note before acknowledging missing ${faceDoc(kind)}.`,
            );
            return;
          }
          const ack: OptionalDocumentAck = {
            kind,
            acknowledgedBy: session.draft.createdBy,
            acknowledgedAt: new Date().toISOString(),
            note,
          };
          session = {
            ...session,
            optionalAcks: [
              ...session.optionalAcks.filter((a) => a.kind !== kind),
              ack,
            ],
          };
          persistSession();
          showBanner(
            "ok",
            `Acknowledged in this browser for missing ${faceDoc(kind)} by ${amVisibleDisplayText(safeText(ack.acknowledgedBy))} (browser-local — does not travel with Submit).`,
          );
          paint();
        });
        ackHost.append(
          h("div", { "data-role": `optional-ack-row-${kind}` }, [
            h("p", { class: "muted" }, [
              `Optional ${faceDoc(kind)} is missing — record an attributed acknowledgement to proceed without it.`,
            ]),
            h("label", { class: "file-label" }, ["Note", noteInput]),
            ackBtn,
          ]),
        );
      }
      docsHost.append(ackHost);
    }
  };

  const syncFormBasis = (next: CompanyBasis): void => {
    state.basis = next;
    for (const value of BASES) {
      const el = basisGroup.querySelector<HTMLInputElement>(
        `[data-role="onboarding-basis-${value}"]`,
      );
      if (el) el.checked = value === next;
    }
    if (session !== null && session.draft.basis !== next) {
      session = {
        ...session,
        draft: { ...session.draft, basis: next },
      };
    }
  };

  const paintDeterminations = (): void => {
    commitDeterminationDecision = recordDeterminationDecision;
    const drafts = snapshotDeterminationDrafts(detHost);
    clear(detHost);
    const proposals = currentProposals();
    detHost.append(
      h("h2", { class: "section-title" }, ["Derived determinations"]),
    );
    if (proposals.length === 0) {
      detHost.append(
        h("p", { class: "muted", "data-role": "determinations-empty" }, [
          "No derived determinations to confirm yet. After equity-feeding documents are uploaded (or an extract feed is supplied), each engine reading appears here beside its source text — Confirm is never pre-selected.",
        ]),
      );
      return;
    }
    for (const det of proposals) {
      const prior = session?.decisions.find((d) => d.field === det.field);
      const extractReady = hasUsableSourceText(det.sourceText);
      if (
        prior &&
        isVerifiedCompletingDecision(prior) &&
        extractReady &&
        needsDualReading(prior, det)
      ) {
        detHost.append(
          renderDualReadingCard(
            det,
            prior,
            (decision, correctedValue, sourceUsed, meta) =>
              recordDeterminationDecision(
                det,
                decision,
                correctedValue,
                sourceUsed,
                meta,
              ),
            cardOptionsFor(det.field),
          ),
        );
        continue;
      }
      if (prior && isVerifiedCompletingDecision(prior)) {
        // Label by what was actually checked (rd round 11, R6-04): a reading
        // typed from the document has no crop and no OCR behind it, so it must
        // not be labelled as a screenshot-crop waiver.
        const evidence =
          prior.quoteCheck?.via === "none"
            ? "Typed by you from the document; not checked against it."
            : prior.quoteVerification === "asserted"
              ? "AM-asserted (screenshot crop) — OCR waiver, not a match against the stored return"
              : prior.quoteCheck?.via === "qb_object"
                ? "QB object quotation (document-verified)"
                : prior.quoteCheck?.via === "extract_span"
                  ? "Engine extract quotation (document-verified)"
                  : "Quotation (document-verified)";
        const determined =
          prior.determinedValue ??
          prior.correctedValue ??
          (prior.decision === "confirm" ? det.candidate : "");
        const shown =
          det.field === "form" ? displayFormType(determined) : determined;
        const decidedQuotation = sourceQuotationDisplay(prior.sourceTextUsed);
        detHost.append(
          h("div", {
            class: "determination-card determination-decided",
            "data-role": "determination-card",
            "data-field": det.field,
            "data-decision": prior.decision,
            "data-quote-verification": prior.quoteVerification ?? "verified",
            ...(prior.quotationProvenance
              ? { "data-quotation-provenance": prior.quotationProvenance }
              : {}),
          }, [
            h("p", { "data-role": "determination-field" }, [faceField(det.field)]),
            h("div", { "data-role": "determination-decision-record" }, [
              `Saved in this draft${shown ? `: ${safeText(shown)}` : ""}.`,
              showDetails(
                `Recorded determination (travels with Submit): ${prior.decision}` +
                  (shown ? ` → ${safeText(shown)}` : "") +
                  ` at ${prior.decidedAt}`,
              ),
            ]),
            det.field === "books_basis"
              ? h("div", {
                  class: "muted",
                  "data-role": "determination-basis-wired",
                }, [
                  `Books basis on the company form is ${state.basis === "" ? "not chosen" : faceValue(state.basis)}.`,
                  showDetails(
                    `Submitting saves ${state.basis || "(not chosen)"} as the company's books setting. Accrual is recorded here as evidence, and the books keep counting money when it moves until accrual bookkeeping is built.`,
                  ),
                ])
              : null,
            h("p", {
              class: "muted",
              "data-role": "determination-quotation-provenance",
            }, [evidence]),
            h("blockquote", {
              class: "determination-source",
              "data-role": "determination-source",
            }, [decidedQuotation.text]),
            decidedQuotation.note,
          ]),
        );
        continue;
      }
      if (prior && isBlockingDisposition(prior.decision)) {
        detHost.append(
          renderBlockedDeterminationCard(
            det,
            prior,
            (decision, correctedValue, sourceUsed, meta) =>
              recordDeterminationDecision(
                det,
                decision,
                correctedValue,
                sourceUsed,
                meta,
              ),
            cardOptionsFor(det.field),
          ),
        );
        continue;
      }
      detHost.append(
        renderDeterminationCard(
          det,
          (decision, correctedValue, sourceUsed, meta) =>
            recordDeterminationDecision(
              det,
              decision,
              correctedValue,
              sourceUsed,
              meta,
            ),
          cardOptionsFor(det.field),
        ),
      );
    }
    restoreDeterminationDrafts(detHost, drafts);

    function cardOptionsFor(field: string): DeterminationCardOptions {
      const docs = session?.documents ?? [];
      const live = session;
      const tax = docs.find((d) => d.kind === "tax-return" && d.r2Key !== null);
      const ocrField: OpeningOcrDeterminationField | null =
        field === "form" || field === "tax_year" || field === "basis"
          ? field
          : null;
      const ocr =
        tax !== undefined &&
        ocrField !== null &&
        live !== null &&
        typeof client.getOpeningDocumentOcr === "function" &&
        typeof client.putOpeningOcrCrop === "function"
          ? {
              contentSha256Hex: tax.contentSha256Hex,
              getOcr: () =>
                client.getOpeningDocumentOcr(
                  live.draft.slug,
                  "tax-return",
                  tax.contentSha256Hex,
                  ocrField,
                ),
              putCrop: (image: Blob) =>
                client.putOpeningOcrCrop(
                  live.draft.slug,
                  tax.contentSha256Hex,
                  image,
                  ocrField,
                ),
            }
          : undefined;
      return {
        candidateDocuments: documentsForDeterminationField(field, docs),
        verifyQuote: async (args) => {
          if (session === null) {
            return { via: "none", reason: "unsearchable" };
          }
          const verifySlug = session.draft.slug;
          let check: QuoteCheck | undefined;
          const outcome = await runWrite({
            key: writeKey(
              `onboarding-quote:${args.contentSha256Hex}:${args.field ?? ""}`,
            ),
            scope: detHost,
            gate: () => null,
            send: async () => {
              check = await client.verifyOpeningDocumentQuote(verifySlug, args);
              return check;
            },
            stillCurrent,
          });
          const verifyError = outcome.kind === "landed" ? undefined : outcome.error;
          if (outcome.kind !== "landed" || check === undefined) {
            throw verifyError instanceof Error
              ? verifyError
              : new Error("Quotation check did not complete.");
          }
          return check;
        },
        ...(ocr !== undefined ? { ocr } : {}),
        canOwnerAttest: role === "owner",
      };
    }

    /**
     * What the server now holds for one field, said plainly. Used after a 409
     * so the AM can see the record that won instead of a bare "try again".
     */
    async function serverDeterminationHeadDetail(
      field: string,
    ): Promise<string | undefined> {
      const slug = session?.draft.slug;
      if (slug === undefined) return undefined;
      if (typeof client.listOpeningDeterminations !== "function") {
        return undefined;
      }
      try {
        const body = await client.listOpeningDeterminations(slug);
        const head = body.determinations
          .filter((row) => row.field === field)
          .sort((a, b) => b.id - a.id)[0];
        if (head === undefined) return undefined;
        // The head value is an API string like any other: determinationValueFace
        // passes it through the AM-visible strip (round 2 and round 3 reviews).
        return `The server has ${faceField(field)}: ${determinationValueFace(field, head.determinedValue ?? "")}, saved ${timeAgoText(head.recordedAt)}.`;
      } catch {
        return undefined;
      }
    }

    /**
     * One field is one decision in flight: the cards guard their own press,
     * and this set is the second, field-scoped gate. A second call for the
     * same field while a PUT is unanswered returns at once instead of sending
     * a second PUT the server may order differently from the browser. Opening
     * equity is one-chance, so the two cannot be allowed to disagree.
     */
    async function recordDeterminationDecision(
      det: DerivedDetermination,
      decision: DeterminationDecisionKind,
      correctedValue: string | undefined,
      sourceUsed: string,
      meta?: DeterminationDecideMeta,
      opts: { accrualWarningAcknowledged?: boolean } = {},
    ): Promise<void> {
      if (determinationsInFlight.has(det.field)) return;
      determinationsInFlight.add(det.field);
      try {
        await performDeterminationDecision(
          det,
          decision,
          correctedValue,
          sourceUsed,
          meta,
          opts,
        );
      } finally {
        determinationsInFlight.delete(det.field);
      }
    }

    async function performDeterminationDecision(
      det: DerivedDetermination,
      decision: DeterminationDecisionKind,
      correctedValue: string | undefined,
      sourceUsed: string,
      meta?: DeterminationDecideMeta,
      runOpts: { accrualWarningAcknowledged?: boolean } = {},
    ): Promise<void> {
      if (session === null) {
        showBanner("err", "Save a draft before recording a determination.");
        return;
      }
      const engineCandidate = snapshotEngineCandidate(det.field, det.candidate);
      let determinedValue: string | null = null;
      if (decision === "confirm") {
        determinedValue = engineCandidate;
        if (determinedValue === null) {
          showBanner(
            "err",
            "Confirm needs a closed-set engine candidate — Correct instead.",
          );
          return;
        }
      } else if (decision === "correct") {
        determinedValue = canonicalDeterminedValue(
          det.field,
          correctedValue ?? "",
        );
        if (determinedValue === null || determinedValue.length === 0) {
          showBanner("err", "Correct needs a determined value.");
          return;
        }
      }

      if (decision === "no_source") {
        const note = (meta?.note ?? "").trim();
        if (!note) {
          showBanner(
            "err",
            "Add a note naming what you checked before recording that no source was found.",
          );
          return;
        }
      }
      if (isCompletingDisposition(decision)) {
        if (
          meta === undefined ||
          (meta.quoteVerification !== "verified" &&
            meta.quoteVerification !== "asserted") ||
          meta.quoteAssertion === undefined
        ) {
          showBanner(
            "err",
            "Quotation must be verified against the stored document, or confirmed from a screenshot crop, before Completing.",
          );
          return;
        }
      }
      if (
        isCompletingDisposition(decision) &&
        (det.field === "basis" || det.field === "books_basis")
      ) {
        const examined = examineCompletingCompanyBasis({
          field: det.field,
          decision,
          determinedValue,
          engineCandidate,
          ...(correctedValue !== undefined ? { correctedValue } : {}),
          proposal: det,
        });
        if (!examined.ok) {
          showBanner(
            "err",
            `Cannot record this basis determination — unparseable or missing. Examined: ${examined.examined}.`,
          );
          return;
        }
        if (
          examined.basis === "accrual" &&
          !operationalAccrualWritesAllowed() &&
          runOpts.accrualWarningAcknowledged !== true
        ) {
          pendingAccrualCompleting = {
            det,
            decision,
            correctedValue,
            sourceUsed,
            ...(meta !== undefined ? { meta } : {}),
          };
          showAccrualWarningDialog();
          return;
        }
      }
      if (det.field === "books_basis") {
        if (decision === "correct") {
          const normalized = (determinedValue ?? "").trim().toLowerCase();
          if (!isCompanyBasisValue(normalized)) {
            showBanner(
              "err",
              "books_basis correction must be cash or accrual — that is what Submit writes to the company row.",
            );
            return;
          }
          if (normalized !== "accrual" || operationalAccrualWritesAllowed()) {
            syncFormBasis(normalized);
          }
          determinedValue = normalized;
        } else if (decision === "confirm") {
          const normalized = (engineCandidate ?? "").trim().toLowerCase();
          if (!isCompanyBasisValue(normalized)) {
            showBanner(
              "err",
              "books_basis Confirm needs a cash or accrual candidate — refuse and Correct instead.",
            );
            return;
          }
          if (normalized !== "accrual" || operationalAccrualWritesAllowed()) {
            syncFormBasis(normalized);
          }
        }
      }

      const putBody: OpeningDeterminationPut = {
        field: det.field,
        action: decision,
        determinedValue,
        engineCandidate,
        quotation: sourceUsed,
        documentKind: meta?.quoteAssertion?.kind ?? null,
        documentContentSha256: meta?.quoteAssertion?.contentSha256Hex ?? null,
        ...(meta?.quoteAssertion?.citation !== undefined
          ? { citation: meta.quoteAssertion.citation }
          : {}),
        ...(meta?.ownerAttest === true ? { ownerAttest: true } : {}),
      };

      const determinationSlug = session.draft.slug;
      const writeOutcome = await runWrite({
        key: writeKey(`onboarding-determination:${det.field}`),
        scope: detHost,
        gate: () => null,
        send: () => client.putOpeningDetermination(determinationSlug, putBody),
        stillCurrent,
      });
      if (!stillCurrent()) return;
      if (writeOutcome.kind !== "landed") {
        const error = writeOutcome.error;
        console.error("opening determination PUT rejected", error);
        if (error instanceof AdminApiError && error.status === 409) {
          // A newer head exists: read the server's record before saying what
          // to do, so the screen is not left asserting a value the server no
          // longer holds. Read-only; never adopts a value into this draft.
          const detail = await serverDeterminationHeadDetail(det.field);
          // Naming "Show details" is only true when the read returned a row
          // (round 3 review): a failed read, an empty read or a client without
          // the list call must not promise description it has no way to show.
          const hasDetail = detail !== undefined && detail !== "";
          showBanner(
            "err",
            hasDetail
              ? DETERMINATION_CONFLICT_COPY
              : DETERMINATION_CONFLICT_NO_DETAIL_COPY,
            detail,
          );
          paint();
        } else {
          const refusal = humanDeterminationError(error, role);
          showBanner("err", refusal.copy, refusal.technicalDetail);
        }
        return;
      }

      if (decision === "no_source") {
        const record: DeterminationDecision = {
          field: det.field,
          decision: "no_source",
          sourceTextUsed: SOURCE_TEXT_UNAVAILABLE,
          decidedAt: new Date().toISOString(),
          note: (meta?.note ?? "").trim(),
          acknowledgedBy: session.draft.createdBy,
          engineCandidate,
        };
        session = {
          ...session,
          decisions: [
            ...session.decisions.filter((d) => d.field !== det.field),
            record,
          ],
        };
        persistSession();
        showBanner(
          "ok",
          `No source found for ${faceField(det.field)}. That does not unlock Submit.`,
          `No source found for ${det.field} recorded by ${amVisibleDisplayText(safeText(record.acknowledgedBy ?? ""))} — does not unlock Submit.`,
        );
        paint();
        return;
      }

      const provenance: QuotationProvenance | undefined =
        decision === "confirm" || decision === "correct"
          ? (meta?.quotationProvenance ??
            (hasUsableSourceText(det.sourceText)
              ? "engine_extract"
              : "am_asserted"))
          : undefined;
      const record: DeterminationDecision = {
        field: det.field,
        decision,
        ...(correctedValue !== undefined ? { correctedValue } : {}),
        ...(determinedValue !== null ? { determinedValue } : {}),
        engineCandidate,
        sourceTextUsed: sourceUsed,
        decidedAt: new Date().toISOString(),
        ...(provenance !== undefined
          ? { quotationProvenance: provenance }
          : {}),
        ...(meta?.quoteVerification === "verified" ||
        meta?.quoteVerification === "asserted"
          ? {
              quoteVerification: meta.quoteVerification,
              quoteAssertion: meta.quoteAssertion,
              ...(meta.quoteCheck !== undefined
                ? { quoteCheck: meta.quoteCheck }
                : {}),
            }
          : {}),
      };
      session = {
        ...session,
        decisions: [
          ...session.decisions.filter((d) => d.field !== det.field),
          record,
        ],
      };
      pendingAccrualCompleting = null;
      hideAccrualWarningDialog();
      persistSession();
      // The write landed. Replace whatever the previous failure said: a 409 and
      // a 422 both leave a banner behind, and leaving it up after a successful
      // second press reads "Yours has not been saved" over a saved reading
      // (round 3 review). `no_source` above already clears it the same way.
      const recordedFace =
        determinedValue !== null
          ? determinationValueFace(det.field, determinedValue)
          : "";
      // Reading accrual is allowed as evidence, but operating on it is not
      // built yet. Say so on the banner that records the value, or the green
      // line claims a change the books will not make (round 4 review).
      const accrualOnCash =
        (det.field === "basis" || det.field === "books_basis") &&
        determinedValue === "accrual" &&
        !operationalAccrualWritesAllowed();
      showBanner(
        "ok",
        decision === "reject"
          ? `Recorded ${faceField(det.field)}: refused. That does not unlock Submit.`
          : `Recorded ${faceField(det.field)}${recordedFace === "" ? "" : `: ${recordedFace}`}.${accrualOnCash ? " The books stay on cash until accrual is built." : ""}`,
      );
      paint();
    }
    commitDeterminationDecision = recordDeterminationDecision;
  };

  accrualWarningCorrectBtn.addEventListener("click", () => {
    pendingAccrualCompleting = null;
    hideAccrualWarningDialog();
  });
  accrualWarningArmBtn.addEventListener("click", () => {
    accrualWarningArmBtn.setAttribute("aria-pressed", "true");
    accrualWarningConfirmBtn.hidden = false;
    accrualWarningConfirmBtn.removeAttribute("hidden");
  });
  accrualWarningConfirmBtn.addEventListener("click", () => {
    const pending = pendingAccrualCompleting;
    if (pending === null) {
      hideAccrualWarningDialog();
      return;
    }
    hideAccrualWarningDialog();
    void commitDeterminationDecision(
      pending.det,
      pending.decision,
      pending.correctedValue,
      pending.sourceUsed,
      pending.meta,
      { accrualWarningAcknowledged: true },
    );
  });

  let deferredAckBy = "";
  let deferredAckAt = "";
  let openingChoice: "" | "pending" | "booked" = "";
  const localOpening = new Map<CompanySlug, "pending" | "booked">();
  const localBooksStart = new Map<CompanySlug, string>();
  let listedCompanies: Company[] = [];
  /**
   * Where the company read stands. "No companies listed." is said only after a
   * read that succeeded with zero companies, never while it is in flight and
   * never after it failed (round eight B5).
   */
  let companiesRead: "loading" | "loaded" | "failed" = "loading";

  const paintCompanyList = (): void => {
    clear(companyListHost);
    companyListHost.append(
      h("h2", { class: "section-title" }, ["Company list"]),
    );
    const seen = new Set<CompanySlug>();
    const rows: { slug: CompanySlug; booksStart: string | null }[] = [];
    for (const company of listedCompanies) {
      seen.add(company.slug);
      const books = company.booksStart ?? localBooksStart.get(company.slug) ?? null;
      rows.push({
        slug: company.slug,
        booksStart: books,
      });
    }
    for (const [slug, books] of localBooksStart) {
      if (seen.has(slug)) continue;
      rows.push({ slug, booksStart: books });
    }
    if (rows.length === 0) {
      if (companiesRead === "loading") {
        companyListHost.append(
          h("p", { class: "muted", role: "status", "data-role": "company-list-loading" }, [
            "Loading companies…",
          ]),
        );
        return;
      }
      if (companiesRead === "failed") {
        const retry = button("Try again", "primary", {
          "data-action": "retry-company-list",
          "aria-label": "Try again: company list",
        });
        retry.addEventListener("click", () => {
          companiesRead = "loading";
          paintCompanyList();
          void refreshCompanies().then(() => {
            if (stillCurrent()) paintCompanyList();
          });
        });
        companyListHost.append(
          h("div", { class: "error-state", role: "alert", "data-role": "company-list-error" }, [
            h("p", {}, ["Couldn't load the company list. Try again."]),
            retry,
          ]),
        );
        return;
      }
      companyListHost.append(
        h("p", { class: "muted", "data-role": "company-list-empty" }, [
          "No companies listed.",
        ]),
      );
      return;
    }
    const list = h("ul", { "data-role": "company-list-items" });
    for (const row of rows) {
      const opening = localOpening.get(row.slug);
      const checklist =
        opts.openChecklist === undefined
          ? null
          : h(
              "button",
              {
                type: "button",
                class: "btn btn-ghost",
                "data-action": "open-company-checklist",
                "data-company-slug": row.slug,
                "aria-label": face.ariaFor(
                  `Open the onboarding checklist for ${face.company(row.slug, companyDirectory(), "sentence")}`,
                ),
              },
              ["Checklist"],
            );
      checklist?.addEventListener("click", () => {
        opts.openChecklist?.(row.slug);
      });
      list.append(
        h("li", { "data-company-slug": row.slug }, [
          // Owner decision 14: the one directory names a company; an unnamed
          // one reads "this company", never its slug.
          face.company(row.slug, companyDirectory(), "sentence"),
          h("span", { "data-role": "company-books-start" }, [
            ` books start ${row.booksStart ?? "not set"}`,
          ]),
          h("span", { "data-role": "company-opening" }, [
            ` opening balances ${opening ?? "not set"}`,
          ]),
          checklist,
        ]),
      );
    }
    companyListHost.append(list);
    if (companiesRead === "failed") {
      // The list above is from an earlier read; say it may be out of date.
      companyListHost.append(
        h("p", { class: "muted", "data-role": "company-list-stale" }, [
          "Couldn't refresh the company list. What is shown may be out of date.",
        ]),
      );
    }
  };

  const paintDeferred = (): void => {
    const fields = companyWriteFieldsFromForm(state);
    const formReady =
      fields !== null && missingDeterminations(state).length === 0;
    const ready = deferredRegistrationReady({
      formReady,
      hasDraft: session !== null,
      booksStart: booksStartInput.value.trim(),
      openingChoice,
      acknowledged: deferredAckBox.checked,
      acknowledgedBy: deferredAckBy,
    });
    clear(deferredReason);
    clear(deferredAckRecord);
    if (deferredAckBy !== "" && deferredAckAt !== "") {
      deferredAckRecord.append(
        document.createTextNode(
          `Opening books deferred — recorded in this browser only, not saved. Noted ${timeAgoText(deferredAckAt)}.`,
        ),
      );
    }
    if (ready) {
      deferredBtn.removeAttribute("disabled");
      deferredReason.append(
        document.createTextNode(
          "Opening books deferred — register with no QuickBooks reports.",
        ),
      );
    } else {
      deferredBtn.setAttribute("disabled", "true");
      deferredReason.append(
        document.createTextNode(
          "Register later stays disabled until a draft is saved, books start is YYYY-MM-DD, opening is pending, and Opening books deferred is checked.",
        ),
      );
    }
  };

  const paintSubmit = (): void => {
    const docs = session?.documents ?? [];
    const acks = session?.optionalAcks ?? [];
    const decisions = session?.decisions ?? [];
    const proposals = currentProposals();
    const gaps = draftGaps({
      state,
      documents: docs,
      optionalAcks: acks,
      proposals,
      decisions,
    });
    const complete = session !== null && draftGapsEmpty(gaps);
    clear(submitReason);
    if (complete) {
      submitBtn.removeAttribute("disabled");
      submitReason.append(
        document.createTextNode(
          "Draft complete — submit promotes into companies.",
        ),
      );
    } else {
      submitBtn.setAttribute("disabled", "true");
      const parts: string[] = [];
      const face: string[] = [];
      if (session === null) {
        parts.push("Save a draft first");
        face.push("Save a draft first");
      }
      if (gaps.determinations.length > 0) {
        parts.push(`missing determinations: ${gaps.determinations.join(", ")}`);
        face.push("Some answers are still missing");
      }
      if (gaps.documents.length > 0) {
        parts.push(`missing required documents: ${gaps.documents.join(", ")}`);
        face.push("A required document is still missing");
      }
      if (gaps.optionalDocuments.length > 0) {
        parts.push(
          `optional documents need acknowledgement: ${gaps.optionalDocuments.join(", ")}`,
        );
        face.push("Confirm the optional documents that you do not have");
      }
      if (gaps.undisposedProposals.length > 0) {
        parts.push(
          `undisposed determinations: ${gaps.undisposedProposals.join(", ")}`,
        );
        face.push("A reading still needs a decision");
      }
      if (gaps.refusedProposals.length > 0) {
        parts.push(
          `refused determinations need Correct: ${gaps.refusedProposals.join(", ")}`,
        );
        face.push("A refused reading still needs a correction");
      }
      if (gaps.noSourceProposals.length > 0) {
        parts.push(
          `no source found — Correct with a quotation required: ${gaps.noSourceProposals.join(", ")}`,
        );
        face.push("A reading still needs the line from the document");
      }
      if (gaps.basisDisagreements.length > 0) {
        parts.push(`basis disagreement: ${gaps.basisDisagreements.join("; ")}`);
        face.push("The basis answers do not agree");
      }
      submitReason.append(
        document.createTextNode(`Submit disabled — ${face.join("; ")}.`),
        showDetails(parts.join("; ")),
      );
    }
  };

  /** Slug is the promote key and cannot travel in CompanyUpdate — lock it
   *  once a draft session exists so Present cannot invent a different one. */
  const syncSlugLock = (): void => {
    if (session !== null) {
      state.slug = session.draft.slug;
      slugInput.value = session.draft.slug;
      slugInput.readOnly = true;
      slugInput.setAttribute("aria-readonly", "true");
    } else {
      slugInput.readOnly = false;
      slugInput.removeAttribute("aria-readonly");
    }
  };

  const paint = (): void => {
    syncSlugLock();
    paintDraftList();
    paintDraftView();
    paintDocuments();
    ensureExtracts();
    paintDeterminations();
    paintSubmit();
    paintDeferred();
    paintCompanyList();
  };

  saveDraftBtn.addEventListener("click", () => {
    void (async () => {
      if (!stillCurrent()) return;
      const missing = missingDeterminations(state);
      if (missing.length > 0) {
        showBanner(
          "err",
          "Cannot save the draft yet. Finish the items still listed as missing.",
          `Cannot save draft — missing: ${missing.join(", ")}`,
        );
        paint();
        return;
      }
      const fields = companyWriteFieldsFromForm(state);
      if (fields === null) {
        showBanner(
          "err",
          "Cannot save the draft yet. Choose the company type and the books basis.",
          "Cannot save draft — companyType and basis are required",
        );
        return;
      }
      // The first save creates the draft; later saves update that draft. A
      // second createCompany was a guaranteed 409 that still told the AM the
      // draft was saved (rd round 11, R6-08).
      const editingExistingDraft = session;
      const body: CompanyCreate = {
        slug: state.slug,
        ...fields,
        asDraft: true,
      };
      let saved: Company | CompanyDraft | undefined;
      const outcome = await runWrite({
        key: writeKey("onboarding-draft"),
        scope: form,
        gate: () => null,
        send: async () => {
          saved =
            editingExistingDraft === null
              ? await client.createCompany(body)
              : await client.updateCompany(
                  editingExistingDraft.draft.slug,
                  fields,
                );
          return saved;
        },
        // The list re-read runs after the write landed: its failure must not
        // read as a failed save (reload-after-write).
        afterLanded: async () => {
          await refreshServerDrafts();
        },
        stillCurrent,
      });
      if (!stillCurrent()) return;
      const saveError = outcome.kind === "landed" ? undefined : outcome.error;
      if (outcome.kind !== "landed" || saved === undefined) {
        const failure = saveDraftFailure(
          saveError,
          editingExistingDraft === null,
          role,
        );
        showBanner("err", failure.sentence, failure.detail ?? undefined);
        paint();
        return;
      }
      const draft = saved;
      if (!isCompanyDraft(draft)) {
        showBanner(
          "err",
          editingExistingDraft === null
            ? "Server returned a live company; expected a draft."
            : DRAFT_GONE_COPY,
        );
        paint();
        return;
      }
      session = {
        draft,
        documents: session?.documents ?? [],
        optionalAcks: session?.optionalAcks ?? [],
        decisions: session?.decisions ?? [],
      };
      applyDraftToForm(draft);
      persistSession();
      showBanner(
        "ok",
        outcome.afterLandedFailed === true
          ? "Draft saved. The saved-drafts list could not refresh."
          : "Draft saved.",
      );
      paint();
    })();
  });

  submitBtn.addEventListener("click", () => {
    void (async () => {
      if (!stillCurrent() || session === null) return;
      const docs = session.documents;
      const acks = session.optionalAcks;
      const decisions = session.decisions;
      const proposals = currentProposals();
      if (
        !isDraftComplete({
          state,
          documents: docs,
          optionalAcks: acks,
          proposals,
          decisions,
        })
      ) {
        paintSubmit();
        return;
      }
      // Full form snapshot — not a selected subset. promoteDraft falls back to
      // the draft row for any omitted key; omitting is how R20O-1 happened.
      // Completing events were already PUT; quoteAssertions is not Completing.
      const promotePatch = promotePatchFromForm(state, decisions);
      if (promotePatch === null) {
        showBanner("err", "Books basis must be cash or accrual before Submit.");
        paintSubmit();
        return;
      }
      if (
        promotePatch.basis === "accrual" &&
        !operationalAccrualWritesAllowed()
      ) {
        showBanner(
          "err",
          ACCRUAL_NOT_IMPLEMENTED_COPY,
        );
        showAccrualWarningDialog();
        paintSubmit();
        return;
      }
      // Promote-vs-abandon race: capture slug; abandon mid-flight must not
      // clear a successful promote's banner with a stale session wipe.
      const promotingSlug = session.draft.slug;
      const uploadedKeys = docs
        .filter((d) => d.r2Key !== null)
        .map((d) => d.r2Key as string);
      const hubAnnotation = promotePatch.hubLink ?? null;
      const submittedType = promotePatch.companyType;
      let result: Company | CompanyDraft | undefined;
      const outcome = await runWrite({
        key: writeKey("onboarding-submit"),
        scope: submitBtn.closest<HTMLElement>("section.card") ?? root,
        gate: () => null,
        send: async () => {
          result = await client.updateCompany(promotingSlug, promotePatch);
          return result;
        },
        stillCurrent,
      });
      if (!stillCurrent()) return;
      const submitError = outcome.kind === "landed" ? undefined : outcome.error;
      if (outcome.kind !== "landed" || result === undefined) {
        const copy = failureCopy(submitError, { role, context: "write" });
        showBanner(
          "err",
          humanSubmitError(submitError, role),
          copy.detail ?? undefined,
        );
        paintSubmit();
        return;
      }
      const registeredCompany = result;
      if (!isCompany(registeredCompany)) {
        showBanner("err", "Submit did not promote to a company.");
        paintSubmit();
        return;
      }
      rememberCompany(registeredCompany);
      draftStore.remove(promotingSlug);
      // Abandon may have nulled/switched session during the await.
      session = clearSessionIfPromotedSlug(session, promotingSlug);
      // Banner names Law-1 fields from the *server* result so a future wire
      // regression cannot hide behind "basis=…" alone.
      const registered = `Submitted ${face.company(registeredCompany.slug, companyDirectory(), "sentence")}. Registered as ${faceValue(registeredCompany.companyType)}, ${faceValue(registeredCompany.basis)} basis, included ${registeredCompany.featurePacks.map((pack) => faceValue(pack)).join(", ")}.`;
      const registeredDetail = `company registered with companyType=${safeText(registeredCompany.companyType)} basis=${safeText(registeredCompany.basis)} featurePacks=${safeText(registeredCompany.featurePacks.join(","))}.`;
      const uploadFace =
        uploadedKeys.length > 0
          ? "Opening documents are already uploaded. This page does not start the opening-books work."
          : "No opening documents were uploaded from this page. This page does not start the opening-books work.";
      const uploadDetail =
        uploadedKeys.length > 0
          ? "Opening documents are already uploaded. Intake still checks them before the opening-books work starts."
          : "No opening documents were uploaded from this page. This page does not start the opening-books work.";
      let hubFace = "";
      let hubDetail = "";
      if (submittedType === "gvpm-practice") {
        if (hubAnnotation) {
          hubFace =
            " The practice id was saved. The live connection was not created. The owner still has to set that up.";
          hubDetail = ` Hub practice-id annotation stored as hub_link=${safeText(hubAnnotation)}; operational Hub link (hub_linked_companies) was not created — owner provisioning still required.`;
        } else {
          hubFace = " No practice id was saved. The live connection was not created.";
          hubDetail =
            " No Hub practice-id annotation was supplied; operational Hub link was not created.";
        }
      }
      const appendRefresh = showRegisteredBanner(
        `${registered} ${uploadFace}${hubFace}`,
        `${registeredDetail} ${uploadDetail}${hubDetail}`,
      );
      paint();
      // Not awaited: the banner and the repaint are already on screen, and a
      // slow or never-settling shell refresh must not hold either (round 4
      // review). The shell's company switcher only learns about this company
      // if it is told (R6-20); the option stays optional (lane R8c wires it).
      void notifyRegistered(registeredCompany.slug).then(appendRefresh);
    })();
  });

  booksStartInput.addEventListener("input", () => {
    paintDeferred();
  });
  openingPending.addEventListener("change", () => {
    openingChoice = openingPending.checked ? "pending" : openingChoice;
    if (openingBooked.checked) openingChoice = "booked";
    paintDeferred();
  });
  openingBooked.addEventListener("change", () => {
    openingChoice = openingBooked.checked ? "booked" : openingChoice;
    if (openingPending.checked) openingChoice = "pending";
    paintDeferred();
  });
  deferredAckBox.addEventListener("change", () => {
    if (!deferredAckBox.checked) {
      deferredAckBy = "";
      deferredAckAt = "";
      paintDeferred();
      return;
    }
    if (session === null) {
      deferredAckBox.checked = false;
      deferredAckBy = "";
      deferredAckAt = "";
      paintDeferred();
      return;
    }
    deferredAckBy = session.draft.createdBy;
    deferredAckAt = new Date().toISOString();
    paintDeferred();
  });
  deferredBtn.addEventListener("click", () => {
    void (async () => {
      if (!stillCurrent() || session === null) return;
      const fields = companyWriteFieldsFromForm(state);
      const formReady =
        fields !== null && missingDeterminations(state).length === 0;
      if (
        !deferredRegistrationReady({
          formReady,
          hasDraft: true,
          booksStart: booksStartInput.value.trim(),
          openingChoice,
          acknowledged: deferredAckBox.checked,
          acknowledgedBy: deferredAckBy,
        }) ||
        fields === null
      ) {
        paintDeferred();
        return;
      }
      const booksStart = booksStartInput.value.trim();
      const promotePatch: CompanyUpdate = {
        submit: true,
        ...fields,
        booksStart,
      };
      const liveSlug = session.draft.slug;
      let result: Company | CompanyDraft | undefined;
      const outcome = await runWrite({
        key: writeKey("onboarding-register-later"),
        scope: deferredBtn.closest<HTMLElement>("section.card") ?? root,
        gate: () => null,
        send: async () => {
          result = await client.updateCompany(liveSlug, promotePatch);
          return result;
        },
        // The company list re-read runs after the write landed: a failed
        // refresh must not read as a failed registration.
        afterLanded: async () => {
          await refreshCompanies();
        },
        stillCurrent,
      });
      if (!stillCurrent()) return;
      const registerError = outcome.kind === "landed" ? undefined : outcome.error;
      if (outcome.kind !== "landed" || result === undefined) {
        const copy = failureCopy(registerError, { role, context: "write" });
        showBanner(
          "err",
          humanSubmitError(registerError, role),
          copy.detail ?? undefined,
        );
        paintDeferred();
        return;
      }
      const registeredCompany = result;
      if (!isCompany(registeredCompany)) {
        showBanner("err", "Server did not return a company.");
        paintDeferred();
        return;
      }
      rememberCompany(registeredCompany);
      localOpening.set(registeredCompany.slug, "pending");
      localBooksStart.set(
        registeredCompany.slug,
        registeredCompany.booksStart ?? booksStart,
      );
      const appendRefresh = showRegisteredBanner(
        `Registered ${face.company(registeredCompany.slug, companyDirectory(), "sentence")} with opening books deferred. The opening balances are still to come.`,
      );
      // Same shell refresh as Submit (R6-20); optional, R8c wires it. The
      // company is registered either way, so a failed refresh only adds a
      // line, and nothing here waits for it (round 4 review).
      void notifyRegistered(registeredCompany.slug).then(appendRefresh);
      session = null;
      paint();
    })();
  });

  paint();
  void Promise.all([refreshServerDrafts(), refreshCompanies()]).then(() => {
    if (stillCurrent()) paint();
  });
}

/** Optional fields passed with a determination Apply. */
export interface DeterminationDecideMeta {
  note?: string;
  quotationProvenance?: QuotationProvenance;
  quoteVerification?: QuoteVerificationStatus;
  quoteAssertion?: OpeningDocumentQuoteAssertion;
  quoteCheck?: QuoteCheck;
  ownerAttest?: boolean;
}

export type DeterminationDecideHandler = (
  decision: DeterminationDecisionKind,
  correctedValue: string | undefined,
  sourceTextUsed: string,
  meta?: DeterminationDecideMeta,
) => void | Promise<void>;

/** Injected Worker verify call (tests mock; page uses AdminClient). */
export type QuoteVerifyFn = (args: {
  kind: OpeningDocumentKind;
  contentSha256Hex: string;
  filename: string;
  quote: string;
  field?: string;
}) => Promise<QuoteCheck>;

export interface DeterminationCardOptions {
  verifyQuote: QuoteVerifyFn;
  candidateDocuments: readonly DraftDocumentRow[];
  /** Tax-return screenshot-crop OCR. Absent for QB / unit tests without OCR. */
  ocr?: {
    contentSha256Hex: string;
    getOcr: () => Promise<OpeningDocumentOcrResponse>;
    putCrop: (image: Blob) => Promise<{ status: "pending" }>;
  };
  /**
   * True only for the owner. Owner attest (the box, the other-value input and
   * "Correct other") exists for the owner alone; absent means not allowed.
   */
  canOwnerAttest?: boolean;
}

function setApplyRefusal(el: HTMLElement, message: string): void {
  el.hidden = false;
  el.textContent = message;
}

function clearApplyRefusal(el: HTMLElement): void {
  el.hidden = true;
  el.textContent = "";
}

/**
 * One press, one determination (rd-r12 B3a-3). This replaces the old
 * `runOnce` card guard: `runWrite` disables the card's controls for the whole
 * press and a second press shares the first flight instead of starting a
 * second one. The write itself is the shorter `send` inside
 * `performDeterminationDecision`, so the two keys never nest on one another.
 */
function pressOnce(
  control: HTMLButtonElement,
  work: () => Promise<unknown>,
  scope?: HTMLElement,
): void {
  const card = scope ?? control.parentElement ?? document.body;
  let pressId = pressWriteKeys.get(card);
  if (pressId === undefined) {
    pressId = ++pressWriteSeq;
    pressWriteKeys.set(card, pressId);
  }
  const hadFocus = document.activeElement === control;
  const running = runWrite({
    key: `onboarding-determination-press:${String(pressId)}`,
    scope: card,
    gate: () => null,
    send: work,
    stillCurrent: () => card.isConnected,
  });
  void running.then((outcome) => {
    if (outcome.kind !== "landed" && outcome.error !== undefined) {
      // The work words its own refusal; this is only the unexpected path.
      console.error("determination press failed after the press was guarded", outcome.error);
    }
    // The old `runOnce` put focus back on the pressed control. Keep that: a
    // keyboard user who pressed Confirm must not land on <body> afterwards.
    const active = document.activeElement;
    if (
      hadFocus &&
      control.isConnected &&
      !control.disabled &&
      (active === null || active === document.body)
    ) {
      control.focus();
    }
  });
}

/**
 * Try candidate documents until one verifies the quote.
 * Unsearchable on every candidate → unsearchable; mix of not_found → not_found
 * if any was searchable; never returns verified unless a doc matched.
 */
export async function verifyQuoteAgainstCandidates(
  quote: string,
  field: string,
  candidates: readonly DraftDocumentRow[],
  verifyQuote: QuoteVerifyFn,
): Promise<
  | { completing: true; check: QuoteCheck; assertion: OpeningDocumentQuoteAssertion }
  | { completing: false; check: QuoteCheck }
> {
  if (candidates.length === 0) {
    return { completing: false, check: { via: "none", reason: "unsearchable" } };
  }
  let last: QuoteCheck = { via: "none", reason: "unsearchable" };
  let sawSearchableMiss = false;
  for (const doc of candidates) {
    const check = await verifyQuote({
      kind: doc.kind,
      contentSha256Hex: doc.contentSha256Hex,
      filename: doc.filename,
      quote,
      field,
    });
    last = check;
    if (quoteCheckIsCompleting(check)) {
      return {
        completing: true,
        check,
        assertion: {
          field,
          kind: doc.kind,
          contentSha256Hex: doc.contentSha256Hex,
          filename: doc.filename,
          quote,
        },
      };
    }
    if (
      (check.via === "extract_span" && !check.hit && check.reason === "not_found") ||
      (check.via === "qb_object" && !check.hit && check.reason === "not_found")
    ) {
      sawSearchableMiss = true;
    }
  }
  if (sawSearchableMiss) {
    return {
      completing: false,
      check: { via: "extract_span", hit: false, reason: "not_found" },
    };
  }
  return { completing: false, check: last };
}

export function quoteApplyRefusalCopy(
  check: QuoteCheck,
  opts?: { ocrCropOffered?: boolean },
): string {
  const cropOffered = opts?.ocrCropOffered !== false;
  if (check.via === "extract_span" && !check.hit && check.reason === "marker") {
    return cropOffered
      ? "The unavailable-source marker is not a Completing span. Paste a screenshot crop of the usual location, or choose No source found if you looked and cannot quote (does not unlock Submit)."
      : OCR_SCAN_MANUAL_QUOTE_NONE_COPY;
  }
  if (
    check.via === "none" ||
    (check.via === "qb_object" && !check.hit && check.reason === "unsearchable")
  ) {
    return cropOffered
      ? "This document has no searchable text (scanned or unextracted). Quotation cannot be verified — paste a screenshot crop of the usual location, or choose No source found if you looked and cannot quote (does not unlock Submit)."
      : OCR_SCAN_MANUAL_QUOTE_NONE_COPY;
  }
  return "That quotation was not found in the stored document. Paste a verbatim span from the document, or choose No source found if you looked and cannot quote.";
}

/**
 * Shown when verifyQuote throws (network / client failure) — never Completing.
 * Must stay distinct from {@link quoteApplyRefusalCopy}("not_found") so an AM
 * cannot read a transient blip as "my quote was wrong" and reach for no_source
 * (fix-r21h error-path pin; orchestrator 2026-08-11).
 */
export const QUOTE_VERIFY_TRANSIENT_REFUSAL =
  "Could not verify quotation — try again, or choose No source found if you looked and cannot quote.";

/** Canonical engine candidate at click, or null when not a closed-set value. */
export function snapshotEngineCandidate(
  field: string,
  raw: string,
): string | null {
  if (field === "form") {
    const compact = canonicalFormType(raw);
    if (compact === "1120S" || compact === "1120" || compact === "1065") {
      return compact;
    }
    return null;
  }
  if (field === "basis" || field === "books_basis") {
    const v = raw.trim().toLowerCase();
    return v === "cash" || v === "accrual" ? v : null;
  }
  if (field === "tax_year") {
    const year = raw.trim();
    return /^[0-9]{4}$/.test(year) ? year : null;
  }
  return null;
}

export function canonicalDeterminedValue(
  field: string,
  raw: string,
): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (field === "form") return canonicalFormType(trimmed);
  if (field === "basis" || field === "books_basis") return trimmed.toLowerCase();
  return trimmed;
}

/** Leftover free-text Completing-refuses until Owner attest (closed-set members do not). */
export function leftoverValueNeedsOwnerAttest(
  field: string,
  raw: string,
): boolean {
  const canonical = canonicalDeterminedValue(field, raw);
  if (canonical === null || canonical.length === 0) return true;
  return snapshotEngineCandidate(field, canonical) === null;
}

function displayProposed(field: string, candidate: string): string {
  if (field === "form") {
    const compact = canonicalFormType(candidate);
    if (compact === "1120S" || compact === "1120" || compact === "1065") {
      return displayFormType(compact);
    }
  }
  return candidate;
}

function closedCorrectChoices(
  field: string,
  engineCanonical: string | null,
): { value: string; label: string }[] {
  if (field === "form") {
    return OPENING_FORM_TYPES.filter((v) => v !== engineCanonical).map((v) => ({
      value: v,
      label: displayFormType(v),
    }));
  }
  if (field === "basis" || field === "books_basis") {
    return (["cash", "accrual"] as const)
      .filter((v) => v !== engineCanonical)
      .map((v) => ({ value: v, label: v }));
  }
  return [];
}

function needsDualReading(
  prior: DeterminationDecision,
  det: DerivedDetermination,
): boolean {
  if (!isVerifiedCompletingDecision(prior)) return false;
  if (!hasUsableSourceText(det.sourceText)) return false;
  if (prior.quoteVerification === "asserted") return true;
  return prior.sourceTextUsed.trim() !== det.sourceText.trim();
}

function metaFromVerified(
  verified: {
    completing: true;
    check: QuoteCheck;
    assertion: OpeningDocumentQuoteAssertion;
  },
  provenance: QuotationProvenance,
  citation?: { page: number; line: string },
): DeterminationDecideMeta {
  const check = verified.check;
  const asserted = check.via === "ocr_artifact";
  return {
    quotationProvenance: provenance,
    quoteVerification: asserted ? "asserted" : "verified",
    quoteAssertion: assertionWithOptionalCitation(verified.assertion, citation),
    quoteCheck: check,
  };
}

function isTaxEquityField(field: string): boolean {
  return field === "form" || field === "tax_year" || field === "basis";
}

function appendEvidenceFirst(
  card: HTMLElement,
  det: DerivedDetermination,
): boolean {
  const usable = hasUsableSourceText(det.sourceText);
  card.append(
    h("p", { "data-role": "determination-field" }, [faceField(det.field)]),
  );
  if (det.locationHint !== undefined && det.locationHint.length > 0) {
    card.append(
      h(
        "p",
        {
          class: "muted determination-location-hint",
          "data-role": "determination-location-hint",
        },
        [det.locationHint],
      ),
    );
  }
  const statusCopy = det.extractLoadError
    ? EXTRACT_STATUS_LOAD_ERROR_COPY
    : extractStatusCopy(det.extractStatus);
  if (statusCopy !== null) {
    card.append(
      h(
        "p",
        {
          class: "muted",
          role: "status",
          "data-role": "determination-extract-status",
          "data-extract-status": det.extractStatus ?? "",
        },
        [statusCopy],
      ),
    );
  }
  const citation =
    det.citation !== undefined
      ? `From page ${String(det.citation.page)} — ${det.citation.line}:`
      : null;
  const rawSource = usable
    ? det.sourceText
    : det.sourceText.trim() || SOURCE_TEXT_UNAVAILABLE;
  const quotation = sourceQuotationDisplay(rawSource);
  card.append(
    h(
      "blockquote",
      {
        class: "determination-source",
        "data-role": "determination-source",
      },
      [
        citation !== null
          ? h("p", { "data-role": "determination-citation" }, [citation])
          : null,
        quotation.text,
      ],
    ),
  );
  if (quotation.note !== null) {
    card.append(quotation.note);
  }
  card.append(
    h("p", { "data-role": "determination-candidate" }, [
      `Proposed: ${safeText(displayProposed(det.field, det.candidate))}`,
    ]),
  );
  return usable;
}

function quoteAssertionFromDoc(
  field: string,
  doc: DraftDocumentRow,
  quote: string,
  provenance: QuotationProvenance,
  citation?: { page: number; line: string },
): OpeningDocumentQuoteAssertion {
  return {
    field,
    kind: doc.kind,
    contentSha256Hex: doc.contentSha256Hex,
    filename: doc.filename,
    quote,
    provenance,
    ...(citation !== undefined ? { citation } : {}),
  };
}

function assertionWithOptionalCitation(
  assertion: OpeningDocumentQuoteAssertion,
  citation?: { page: number; line: string },
): OpeningDocumentQuoteAssertion {
  return citation !== undefined ? { ...assertion, citation } : assertion;
}

function appendOcrFallback(
  det: DerivedDetermination,
  ocr: NonNullable<DeterminationCardOptions["ocr"]>,
  applyRefusal: HTMLElement,
  confirmRefused?: HTMLElement,
): { host: HTMLElement; reading: () => string; scanManual: () => boolean } {
  const host = h("div", {
    class: "determination-ocr",
    "data-role": "determination-ocr",
  });
  const preview = h("img", {
    class: "determination-ocr-preview",
    "data-role": "determination-ocr-preview",
    alt: "Screenshot crop you provided",
    hidden: "true",
  });
  const caption = h(
    "p",
    {
      class: "muted",
      "data-role": "determination-ocr-caption",
      hidden: "true",
    },
    ["You provided this image; we read it as:"],
  );
  const textArea = h("textarea", {
    class: "input",
    "aria-label": face.ariaFor(`OCR reading for ${faceField(det.field)}`),
    "data-role": "determination-ocr-text",
    rows: "3",
    placeholder:
      "OCR text appears here after the crop is read — edit if it misread",
  });
  const status = h("p", {
    class: "muted",
    role: "status",
    "data-role": "determination-ocr-status",
  });
  const cropInput = h("input", {
    type: "file",
    accept: "image/png,image/jpeg,image/webp",
    "aria-label": face.ariaFor(`Screenshot crop for ${faceField(det.field)}`),
    "data-role": "determination-ocr-crop",
  });
  const intro = h("p", {
    class: "muted",
    "data-role": "determination-ocr-intro",
    hidden: "true",
  }, [
    "Extraction could not quote this reading. Paste a screenshot crop of the usual location. Confirm the text; it is never auto-applied.",
  ]);
  const cropActions = h("div", {
    class: "determination-ocr-actions",
    "data-role": "determination-ocr-actions",
    hidden: "true",
  }, [cropInput]);
  host.setAttribute("data-ocr-crop-offered", "false");

  let objectUrl: string | null = null;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let lastScanManual = false;

  const setStatus = (message: string): void => {
    status.textContent = message;
  };

  const showCrop = (blob: Blob): void => {
    if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    if (typeof URL.createObjectURL !== "function") return;
    objectUrl = URL.createObjectURL(blob);
    preview.src = objectUrl;
    preview.hidden = false;
    preview.removeAttribute("hidden");
  };

  const applyOcrView = (view: OpeningDocumentOcrResponse): "stop" | "continue" => {
    status.setAttribute("data-ocr-status", view.status);
    if (view.failureReason !== null) {
      status.setAttribute("data-ocr-failure-reason", view.failureReason);
    } else {
      status.removeAttribute("data-ocr-failure-reason");
    }
    const offered = ocrCropOffered(view);
    lastScanManual = view.failureReason === "skipped_no_secret";
    cropActions.hidden = !offered;
    intro.hidden = !offered;
    host.setAttribute("data-ocr-crop-offered", offered ? "true" : "false");
    if (confirmRefused !== undefined) {
      confirmRefused.textContent = offered
        ? "Confirm is refused — no cited span. Paste a screenshot crop of the usual location, Correct with the reading you confirm, or choose No source found (does not unlock Submit)."
        : OCR_SCAN_MANUAL_CONFIRM_REFUSED_COPY;
    }
    if (!offered) {
      textArea.placeholder =
        "Type the value exactly as it appears on the document";
      // Neutral path — never error/alert styling. Manual read is normal.
      status.className = "muted";
    }
    if (view.status === "ready" && typeof view.text === "string") {
      caption.hidden = false;
      caption.removeAttribute("hidden");
      if (textArea.value.trim().length === 0) {
        textArea.value = view.text;
      }
    }
    setStatus(ocrStatusCopy(view));
    return ocrPollShouldContinue(view);
  };

  const pollOcr = (): void => {
    if (pollTimer !== null) clearTimeout(pollTimer);
    let ticks = 0;
    const tick = (): void => {
      if (!host.isConnected) return;
      void ocr
        .getOcr()
        .then((view) => {
          if (!host.isConnected) return;
          const next = applyOcrView(view);
          if (next !== "continue") return;
          ticks += 1;
          if (ticks >= OCR_POLL_MAX_TICKS) {
            setStatus(
              "No OCR result yet after waiting. This card will check again if it is rebuilt. Type the reading from your screenshot, or choose No source found.",
            );
            return;
          }
          pollTimer = setTimeout(tick, OCR_POLL_INTERVAL_MS);
        })
        .catch(() => {
          if (!host.isConnected) return;
          setStatus("Could not load OCR status — try again.");
        });
    };
    tick();
  };

  const submitCrop = (blob: Blob): void => {
    showCrop(blob);
    caption.hidden = true;
    textArea.value = "";
    setStatus("Uploading crop…");
    void ocr
      .putCrop(blob)
      .then(() => {
        if (!host.isConnected) return;
        pollOcr();
      })
      .catch((error: unknown) => {
        if (!host.isConnected) return;
        setApplyRefusal(applyRefusal, humanCropUploadError(error));
        setStatus("Crop upload failed.");
      });
  };

  cropInput.addEventListener("change", () => {
    const file = cropInput.files?.[0];
    if (file === undefined || file.size <= 0) return;
    clearApplyRefusal(applyRefusal);
    submitCrop(file);
  });

  const pasteBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "determination-ocr-paste",
      "aria-label": face.ariaFor(`Paste screenshot crop for ${faceField(det.field)}`),
    },
    ["Paste screenshot crop"],
  );
  pasteBtn.addEventListener("click", () => {
    void (async () => {
      clearApplyRefusal(applyRefusal);
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const type = item.types.find((t) => t.startsWith("image/"));
          if (type === undefined) continue;
          const blob = await item.getType(type);
          submitCrop(blob);
          return;
        }
        setApplyRefusal(
          applyRefusal,
          "No image on the clipboard — copy a screenshot crop and try again.",
        );
      } catch {
        setApplyRefusal(
          applyRefusal,
          "Could not read a screenshot from the clipboard — choose a file instead.",
        );
      }
    })();
  });

  cropActions.append(pasteBtn);

  host.append(intro, cropActions, preview, status, caption, textArea);

  // First GET must run after this host is in the document. Calling pollOcr
  // here hits `if (!host.isConnected) return` and never resumes — arm (a).
  queueMicrotask(() => {
    pollOcr();
  });

  return {
    host,
    reading: () => textArea.value.trim(),
    scanManual: () => lastScanManual,
  };
}

async function resolveCompletingQuote(
  det: DerivedDetermination,
  usable: boolean,
  cardOpts: DeterminationCardOptions,
  ocrReading: string | null,
  pasted: string,
  scanManual = false,
): Promise<
  | { ok: true; quote: string; meta: DeterminationDecideMeta }
  | { ok: false; message: string }
> {
  const refusalOpts = { ocrCropOffered: !scanManual };
  if (usable) {
    const quote = det.sourceText.trim();
    const verified = await verifyQuoteAgainstCandidates(
      quote,
      det.field,
      cardOpts.candidateDocuments,
      cardOpts.verifyQuote,
    );
    if (!verified.completing) {
      return {
        ok: false,
        message: quoteApplyRefusalCopy(verified.check, refusalOpts),
      };
    }
    return {
      ok: true,
      quote,
      meta: metaFromVerified(verified, "engine_extract", det.citation),
    };
  }
  if (ocrReading !== null) {
    if (!hasUsableSourceText(ocrReading)) {
      return {
        ok: false,
        message: scanManual
          ? "Type the value exactly as it appears on the document, then Correct."
          : "Confirm the OCR reading beside the crop (edit if it misread), then Correct. Nothing is auto-applied.",
      };
    }
    const doc = cardOpts.candidateDocuments[0];
    if (doc === undefined) {
      return { ok: false, message: "No tax return is bound to this reading." };
    }
    const verified = await verifyQuoteAgainstCandidates(
      ocrReading,
      det.field,
      cardOpts.candidateDocuments,
      cardOpts.verifyQuote,
    );
    if (verified.completing && verified.check.via === "ocr_artifact") {
      return {
        ok: true,
        quote: ocrReading,
        meta: metaFromVerified(verified, "am_asserted"),
      };
    }
    if (verified.completing) {
      return {
        ok: true,
        quote: ocrReading,
        meta: metaFromVerified(verified, "am_asserted", det.citation),
      };
    }
    // Typed scan: verify-quote stays `{via:"none"}`. Completing is PUT's job.
    if (scanManual && verified.check.via === "none") {
      return {
        ok: true,
        quote: ocrReading,
        meta: {
          quotationProvenance: "am_asserted",
          quoteVerification: "asserted",
          quoteAssertion: quoteAssertionFromDoc(
            det.field,
            doc,
            ocrReading,
            "am_asserted",
          ),
          quoteCheck: verified.check,
        },
      };
    }
    return {
      ok: false,
      message: quoteApplyRefusalCopy(verified.check, refusalOpts),
    };
  }
  if (!hasUsableSourceText(pasted)) {
    return {
      ok: false,
      message:
        "Paste a verbatim quotation from the document, or choose No source found if you looked and cannot quote.",
    };
  }
  const verified = await verifyQuoteAgainstCandidates(
    pasted,
    det.field,
    cardOpts.candidateDocuments,
    cardOpts.verifyQuote,
  );
  if (!verified.completing) {
    return {
      ok: false,
      message: quoteApplyRefusalCopy(verified.check, refusalOpts),
    };
  }
  return {
    ok: true,
    quote: pasted,
    meta: metaFromVerified(verified, "am_asserted"),
  };
}

/** Said to the account manager where the owner would see the other-value box. */
export const OTHER_VALUE_OWNER_ONLY_COPY = advice(
  "confirm_opening_determination",
  "account_manager",
);

function appendClosedSetChoices(
  host: HTMLElement,
  det: DerivedDetermination,
  engineCanonical: string | null,
  onCorrectValue: (value: string, ownerAttest: boolean, pressed: HTMLButtonElement) => void,
  canOwnerAttest: boolean,
): void {
  // Owner attest: a value outside the closed set. The Worker accepts it only
  // from the owner, so the account manager is never shown the way in.
  const ownerAttestControls = (): HTMLElement[] => {
    if (!canOwnerAttest) {
      return [
        h("p", { class: "muted", "data-role": "determination-other-owner-note" }, [
          OTHER_VALUE_OWNER_ONLY_COPY,
        ]),
      ];
    }
    const leftover = h("input", {
      type: "text",
      class: "input",
      "aria-label": face.ariaFor(`Other value for ${faceField(det.field)} (Owner attest)`),
      "data-role": "determination-correct-value",
      placeholder: "Other value (Owner attest — do not paraphrase)",
    });
    const attest = h("input", {
      type: "checkbox",
      "data-role": "determination-owner-attest",
      "aria-label": "Owner attest for a value outside the closed set",
    });
    const leftoverBtn = h(
      "button",
      {
        type: "button",
        class: "btn",
        "data-role": "determination-correct",
        "data-action": "determination-correct",
        "aria-label": face.ariaFor(`Correct ${faceField(det.field)} with Owner-attested other value`),
        "aria-pressed": "false",
      },
      ["Correct other (Owner attest)"],
    );
    leftoverBtn.addEventListener("click", () => {
      const raw = leftover.value.trim();
      if (!raw) return;
      const attested = attest instanceof HTMLInputElement && attest.checked;
      onCorrectValue(raw, attested, leftoverBtn);
    });
    return [leftover, attest, leftoverBtn];
  };

  if (det.field === "tax_year") {
    const yearInput = h("input", {
      type: "number",
      class: "input",
      min: "1900",
      max: "2100",
      step: "1",
      "aria-label": face.ariaFor(`Correct tax year for ${faceField(det.field)}`),
      "data-role": "determination-tax-year",
      placeholder: "Year (YYYY)",
    });
    const yearBtn = h(
      "button",
      {
        type: "button",
        class: "btn",
        "data-role": "determination-correct-choice",
        "data-action": "determination-correct",
        "data-value": "year",
        "aria-label": face.ariaFor(`Correct ${faceField(det.field)} to entered year`),
        "aria-pressed": "false",
      },
      ["Correct year"],
    );
    yearBtn.addEventListener("click", () => {
      const year = yearInput.value.trim();
      if (!/^[0-9]{4}$/.test(year)) return;
      onCorrectValue(year, false, yearBtn);
    });
    host.append(yearInput, yearBtn, ...ownerAttestControls());
    return;
  }

  for (const choice of closedCorrectChoices(det.field, engineCanonical)) {
    const btn = h(
      "button",
      {
        type: "button",
        class: "btn",
        "data-role": "determination-correct-choice",
        "data-action": "determination-correct",
        "data-value": choice.value,
        "aria-label": face.ariaFor(`Correct ${faceField(det.field)} to ${choice.label}`),
        "aria-pressed": "false",
      },
      [`Correct: ${choice.label}`],
    );
    btn.addEventListener("click", () => {
      onCorrectValue(choice.value, false, btn);
    });
    host.append(btn);
  }
  host.append(...ownerAttestControls());
}

export function renderDualReadingCard(
  det: DerivedDetermination,
  prior: DeterminationDecision,
  onDecide: DeterminationDecideHandler,
  cardOpts: DeterminationCardOptions,
): HTMLElement {
  const card = h("div", {
    class: "determination-card determination-dual-reading",
    "data-role": "determination-card",
    "data-field": det.field,
    "data-dual-reading": "true",
  });
  appendEvidenceFirst(card, det);
  const amValue =
    prior.determinedValue ??
    prior.correctedValue ??
    (prior.decision === "confirm" ? det.candidate : "");
  const amShown = det.field === "form" ? displayFormType(amValue) : amValue;
  const engineShown = displayProposed(det.field, det.candidate);
  // Source text beside every determination (CLAUDE.md opening-books rule):
  // the dual card shows the AM's own quotation, not only the extract's.
  const decidedQuotation = sourceQuotationDisplay(prior.sourceTextUsed);
  card.append(
    h("p", { "data-role": "determination-dual-am" }, [
      prior.quoteCheck?.via === "ocr_artifact"
        ? `You recorded ${safeText(amShown)} from a crop ${timeAgoText(prior.decidedAt)}.`
        : `You recorded ${safeText(amShown)} ${timeAgoText(prior.decidedAt)}.`,
    ]),
    h("div", {
      class: "determination-source-block",
      "data-role": "determination-dual-am-source",
    }, [
      h("blockquote", { class: "determination-source" }, [decidedQuotation.text]),
      decidedQuotation.note,
    ]),
    h("p", { "data-role": "determination-dual-engine" }, [
      `The extract now reads ${safeText(engineShown)}. Confirm the extract, or keep your reading (one click). Neither is pre-selected.`,
    ]),
  );
  const applyRefusal = h("p", {
    class: "muted",
    role: "alert",
    "data-role": "determination-apply-refused",
    hidden: "true",
  });
  const keepBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-role": "determination-keep-am",
      "data-action": "determination-keep-am",
      "aria-pressed": "false",
    },
    ["Keep my reading"],
  );
  const confirmExtractBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-role": "determination-confirm",
      "data-action": "determination-confirm",
      "aria-pressed": "false",
    },
    ["Confirm extract"],
  );
  // Both buttons are one card: Keep and Confirm extract used to fire
  // concurrently and the second PUT hit the supersedes UNIQUE (rd round 11,
  // R6-03). The card guard locks both while either is in flight, and the
  // onDecide promise is awaited so the lock lasts until the PUT answers.
  keepBtn.addEventListener("click", () => {
    pressOnce(
      keepBtn,
      async () => {
        try {
          clearApplyRefusal(applyRefusal);
          const quote = prior.sourceTextUsed;
          const verified = await verifyQuoteAgainstCandidates(
            quote,
            det.field,
            cardOpts.candidateDocuments,
            cardOpts.verifyQuote,
          );
          const engine = snapshotEngineCandidate(det.field, det.candidate);
          const determined = canonicalDeterminedValue(det.field, amValue) ?? amValue;
          const action =
            engine !== null && determined === engine ? "confirm" : "correct";
          if (!verified.completing) {
            setApplyRefusal(
              applyRefusal,
              quoteApplyRefusalCopy(verified.check, {
                ocrCropOffered: prior.quoteCheck?.via === "ocr_artifact",
              }),
            );
            return;
          }
          const meta = metaFromVerified(
            verified,
            prior.quotationProvenance ?? "am_asserted",
            det.citation,
          );
          await onDecide(action, determined, quote, meta);
        } catch (error) {
          console.error("keep AM reading rejected", error);
          setApplyRefusal(applyRefusal, quoteVerifyFailureCopy(error));
        }
      },
      card,
    );
  });
  confirmExtractBtn.addEventListener("click", () => {
    pressOnce(
      confirmExtractBtn,
      async () => {
        try {
          clearApplyRefusal(applyRefusal);
          const quote = det.sourceText.trim();
          const verified = await verifyQuoteAgainstCandidates(
            quote,
            det.field,
            cardOpts.candidateDocuments,
            cardOpts.verifyQuote,
          );
          if (!verified.completing) {
            setApplyRefusal(applyRefusal, quoteApplyRefusalCopy(verified.check));
            return;
          }
          await onDecide(
            "confirm",
            undefined,
            quote,
            metaFromVerified(verified, "engine_extract", det.citation),
          );
        } catch (error) {
          console.error("confirm extract rejected", error);
          setApplyRefusal(applyRefusal, quoteVerifyFailureCopy(error));
        }
      },
      card,
    );
  });
  card.append(
    h("div", { class: "determination-choices", "data-role": "determination-choices" }, [
      keepBtn,
      confirmExtractBtn,
      applyRefusal,
    ]),
  );
  return card;
}

/**
 * Evidence-first card: location hint → source + citation → proposed answer
 * → Confirm one click on the candidate; Correct one click per other set member.
 * Confirm is never pre-selected. Correct is never pre-filled with the engine candidate.
 */
export function renderDeterminationCard(
  det: DerivedDetermination,
  onDecide: DeterminationDecideHandler,
  cardOpts: DeterminationCardOptions,
): HTMLElement {
  const card = h("div", {
    class: "determination-card",
    "data-role": "determination-card",
    "data-field": det.field,
  });
  const usable = appendEvidenceFirst(card, det);
  const needsOcr =
    !usable && isTaxEquityField(det.field) && cardOpts.ocr !== undefined;
  const needsPaste = !usable && det.field === "books_basis";

  let confirmRefused: HTMLElement | undefined;
  if (!usable) {
    confirmRefused = h(
      "p",
      {
        class: "muted",
        role: "status",
        "data-role": "determination-confirm-refused",
      },
      [
        needsOcr
          ? OCR_SCAN_MANUAL_CONFIRM_REFUSED_COPY
          : "Confirm is refused — no usable source quotation. Paste the verbatim span from the document beside Correct, choose No source found (records that you looked; does not unlock Submit), or Reject this reading (Reject does not unlock Submit — Correct is still required).",
      ],
    );
    card.append(confirmRefused);
  }

  const confirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-role": "determination-confirm",
      "data-action": "determination-confirm",
      "aria-label": face.ariaFor(`Confirm ${faceField(det.field)}`),
      "aria-pressed": "false",
      ...(usable ? {} : { disabled: "true" }),
    },
    ["Confirm"],
  );
  const rejectBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-role": "determination-reject",
      "data-action": "determination-reject",
      "aria-label": face.ariaFor(`Reject ${faceField(det.field)}`),
      "aria-pressed": "false",
    },
    ["Reject"],
  );
  const noSourceBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-role": "determination-no-source",
      "data-action": "determination-no-source",
      "aria-label": face.ariaFor(`No source found for ${faceField(det.field)}`),
      "aria-pressed": "false",
    },
    ["No source found"],
  );

  const amSourceInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": face.ariaFor(`Source quotation for ${faceField(det.field)}`),
    "data-role": "determination-am-source",
    placeholder: "Paste verbatim source text from the document",
    ...(needsPaste ? {} : { hidden: "true" }),
  });

  const noSourceNoteInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": face.ariaFor(`No-source note for ${faceField(det.field)}`),
    "data-role": "determination-no-source-note",
    placeholder:
      "What you checked (required) — e.g. sealed QB P&L has no basis line",
  });

  const applyRefusal = h("p", {
    class: "muted",
    role: "alert",
    "data-role": "determination-apply-refused",
    hidden: "true",
  });

  const ocrUi =
    needsOcr && cardOpts.ocr !== undefined
      ? appendOcrFallback(det, cardOpts.ocr, applyRefusal, confirmRefused)
      : null;

  const engineCanonical = snapshotEngineCandidate(det.field, det.candidate);
  const choicesHost = h("div", {
    class: "determination-correct-choices",
    "data-role": "determination-correct-choices",
  });

  const applyCorrect = (corrected: string, ownerAttest: boolean, pressed: HTMLButtonElement): void => {
    // A value outside the closed set is refused before any press lock: the
    // check is local, and disabling the card for it made the person wait for a
    // sentence the page already knew.
    if (leftoverValueNeedsOwnerAttest(det.field, corrected) && !ownerAttest) {
      setApplyRefusal(
        applyRefusal,
        "This value is not in the list. Tick Owner attest to record it.",
      );
      return;
    }
    // One press, one determination — and no overlap with Confirm or Reject in
    // flight, because all three are guarded over the whole card (rd round 10
    // review, MUST-FIX 4b). Correct is the same class of money write.
    pressOnce(
      pressed,
      async () => {
        try {
          clearApplyRefusal(applyRefusal);
          const resolved = await resolveCompletingQuote(
            det,
            usable,
            cardOpts,
            ocrUi !== null ? ocrUi.reading() : null,
            amSourceInput.value.trim(),
            ocrUi !== null ? ocrUi.scanManual() : false,
          );
          if (!resolved.ok) {
            setApplyRefusal(applyRefusal, resolved.message);
            return;
          }
          await onDecide("correct", corrected, resolved.quote, {
            ...resolved.meta,
            ...(ownerAttest ? { ownerAttest: true } : {}),
          });
        } catch (error) {
          console.error("determination apply rejected", error);
          setApplyRefusal(applyRefusal, quoteVerifyFailureCopy(error));
        }
      },
      card,
    );
  };

  appendClosedSetChoices(
    choicesHost,
    det,
    engineCanonical,
    applyCorrect,
    cardOpts.canOwnerAttest === true,
  );

  confirmBtn.addEventListener("click", () => {
    if (!usable) return;
    // The same guard as Reject and Correct: one card, one record (rd round 10
    // review, MUST-FIX 4b — Reject used to overlap this in flight).
    pressOnce(
      confirmBtn,
      async () => {
        clearApplyRefusal(applyRefusal);
        try {
          const resolved = await resolveCompletingQuote(
            det,
            true,
            cardOpts,
            null,
            "",
          );
          if (!resolved.ok) {
            setApplyRefusal(applyRefusal, resolved.message);
            return;
          }
          await onDecide("confirm", undefined, resolved.quote, resolved.meta);
        } catch (error) {
          console.error("determination apply rejected", error);
          setApplyRefusal(applyRefusal, quoteVerifyFailureCopy(error));
        }
      },
      card,
    );
  });

  rejectBtn.addEventListener("click", () => {
    // One press, one record: two clicks used to PUT the same rejection twice
    // (rd round 10). The guard covers the whole card, so it also stops a
    // Reject landing while Confirm or Correct is in flight.
    // The sentence recorded is the proposal's own source text (or the explicit
    // "no source" marker); nothing here is shown, and the guard's job is only
    // to stop a second press recording it twice.
    const rejectedSource = usable ? det.sourceText.trim() : SOURCE_TEXT_UNAVAILABLE;
    pressOnce(
      rejectBtn,
      async () => {
        await onDecide("reject", undefined, rejectedSource);
      },
      card,
    );
  });

  noSourceBtn.addEventListener("click", () => {
    pressOnce(
      noSourceBtn,
      async () => {
        clearApplyRefusal(applyRefusal);
        const note = noSourceNoteInput.value.trim();
        if (!note) {
          setApplyRefusal(
            applyRefusal,
            "Add a note naming what you checked before recording that no source was found.",
          );
          return;
        }
        await onDecide("no_source", undefined, SOURCE_TEXT_UNAVAILABLE, { note });
      },
      card,
    );
  });

  card.append(
    h(
      "div",
      { class: "determination-choices", "data-role": "determination-choices" },
      [
        confirmBtn,
        choicesHost,
        rejectBtn,
        noSourceBtn,
        needsPaste ? amSourceInput : null,
        ocrUi?.host ?? null,
        noSourceNoteInput,
        applyRefusal,
      ],
    ),
  );
  return card;
}

/**
 * After Reject or no_source: reading stays blocking. Correct remains on the
 * card so the right path is not costlier than Correcting from the open card
 * (mirror). Confirm stays one click when source is usable.
 */
export function renderBlockedDeterminationCard(
  det: DerivedDetermination,
  prior: DeterminationDecision,
  onDecide: DeterminationDecideHandler,
  cardOpts: DeterminationCardOptions,
): HTMLElement {
  const kind = prior.decision === "no_source" ? "no_source" : "reject";
  const card = h("div", {
    class:
      kind === "no_source"
        ? "determination-card determination-no-source"
        : "determination-card determination-refused",
    "data-role": "determination-card",
    "data-field": det.field,
    "data-decision": kind,
  });
  const usable = appendEvidenceFirst(card, det);
  const needsOcr =
    !usable && isTaxEquityField(det.field) && cardOpts.ocr !== undefined;
  const needsPaste = !usable && det.field === "books_basis";

  const statusText =
    kind === "no_source"
      ? `No source found in this browser` +
        (prior.acknowledgedBy
          ? ` by ${amVisibleDisplayText(safeText(prior.acknowledgedBy))}`
          : "") +
        (prior.note ? ` — note: ${amVisibleDisplayText(safeText(prior.note))}` : "") +
        " — does not unlock Submit. Correct with a quotation to proceed."
      : "Refused in this browser — does not unlock Submit. Correct with a quotation to proceed, or record No source found if you looked and cannot quote.";

  card.append(
    h(
      "div",
      {
        role: "status",
        "data-role": "determination-decision-record",
      },
      [statusText, showDetails(`at ${prior.decidedAt}`)],
    ),
  );

  const amSourceInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": face.ariaFor(`Source quotation for ${faceField(det.field)}`),
    "data-role": "determination-am-source",
    placeholder: "Paste verbatim source text from the document",
    ...(needsPaste ? {} : { hidden: "true" }),
  });

  const noSourceNoteInput = h("input", {
    type: "text",
    class: "input",
    "aria-label": face.ariaFor(`No-source note for ${faceField(det.field)}`),
    "data-role": "determination-no-source-note",
    placeholder: "What you checked (required)",
    ...(kind === "no_source" ? { hidden: "true" } : {}),
  });

  const applyRefusal = h("p", {
    class: "muted",
    role: "alert",
    "data-role": "determination-apply-refused",
    hidden: "true",
  });

  const ocrUi =
    needsOcr && cardOpts.ocr !== undefined
      ? appendOcrFallback(det, cardOpts.ocr, applyRefusal)
      : null;

  const confirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "determination-confirm",
      "data-role": "determination-confirm",
      "aria-label": face.ariaFor(`Confirm ${faceField(det.field)} after ${kind}`),
      "aria-pressed": "false",
      ...(usable ? {} : { disabled: "true", hidden: "true" }),
    },
    ["Confirm"],
  );

  const noSourceBtn =
    kind === "reject"
      ? h(
          "button",
          {
            type: "button",
            class: "btn",
            "data-action": "determination-no-source",
            "data-role": "determination-no-source",
            "aria-label": face.ariaFor(`Record no source found for ${faceField(det.field)}`),
            "aria-pressed": "false",
          },
          ["No source found"],
        )
      : null;

  const engineCanonical = snapshotEngineCandidate(det.field, det.candidate);
  const choicesHost = h("div", {
    class: "determination-correct-choices",
    "data-role": "determination-correct-choices",
  });
  const applyCorrect = (
    corrected: string,
    ownerAttest: boolean,
    pressed: HTMLButtonElement,
  ): void => {
    // One press, one determination on a blocked card too: Correct, Confirm and
    // No source share the card guard, so a second press while the PUT is
    // pending cannot record the same field twice (rd round 11, R6-03).
    if (leftoverValueNeedsOwnerAttest(det.field, corrected) && !ownerAttest) {
      setApplyRefusal(
        applyRefusal,
        "This value is not in the list. Tick Owner attest to record it.",
      );
      return;
    }
    pressOnce(
      pressed,
      async () => {
        try {
          clearApplyRefusal(applyRefusal);
          const resolved = await resolveCompletingQuote(
            det,
            usable,
            cardOpts,
            ocrUi !== null ? ocrUi.reading() : null,
            amSourceInput.value.trim(),
            ocrUi !== null ? ocrUi.scanManual() : false,
          );
          if (!resolved.ok) {
            setApplyRefusal(applyRefusal, resolved.message);
            return;
          }
          await onDecide("correct", corrected, resolved.quote, {
            ...resolved.meta,
            ...(ownerAttest ? { ownerAttest: true } : {}),
          });
        } catch (error) {
          console.error("determination correct rejected", error);
          setApplyRefusal(applyRefusal, quoteVerifyFailureCopy(error));
        }
      },
      card,
    );
  };
  appendClosedSetChoices(
    choicesHost,
    det,
    engineCanonical,
    applyCorrect,
    cardOpts.canOwnerAttest === true,
  );

  confirmBtn.addEventListener("click", () => {
    if (!usable) return;
    pressOnce(
      confirmBtn,
      async () => {
        clearApplyRefusal(applyRefusal);
        try {
          const resolved = await resolveCompletingQuote(
            det,
            true,
            cardOpts,
            null,
            "",
          );
          if (!resolved.ok) {
            setApplyRefusal(applyRefusal, resolved.message);
            return;
          }
          await onDecide("confirm", undefined, resolved.quote, resolved.meta);
        } catch (error) {
          console.error("determination confirm rejected", error);
          setApplyRefusal(applyRefusal, quoteVerifyFailureCopy(error));
        }
      },
      card,
    );
  });

  if (noSourceBtn) {
    noSourceBtn.addEventListener("click", () => {
      clearApplyRefusal(applyRefusal);
      const note = noSourceNoteInput.value.trim();
      if (!note) {
        noSourceNoteInput.removeAttribute("hidden");
        setApplyRefusal(
          applyRefusal,
          "Add a note naming what you checked before recording that no source was found.",
        );
        return;
      }
      pressOnce(
        noSourceBtn,
        async () => {
          await onDecide("no_source", undefined, SOURCE_TEXT_UNAVAILABLE, {
            note,
          });
        },
        card,
      );
    });
  }

  card.append(
    h(
      "div",
      { class: "determination-choices", "data-role": "determination-choices" },
      [
        usable ? confirmBtn : null,
        choicesHost,
        needsPaste ? amSourceInput : null,
        ocrUi?.host ?? null,
        kind === "reject" ? noSourceNoteInput : null,
        applyRefusal,
        noSourceBtn,
      ],
    ),
  );
  return card;
}

/**
 * @deprecated Use {@link renderBlockedDeterminationCard}. Kept as a named
 * alias so older test imports keep resolving during the rename.
 */
export function renderRefusedDeterminationCard(
  det: DerivedDetermination,
  prior: DeterminationDecision,
  onDecide: DeterminationDecideHandler,
  cardOpts: DeterminationCardOptions,
): HTMLElement {
  return renderBlockedDeterminationCard(det, prior, onDecide, cardOpts);
}

function isCompanyDraft(value: Company | CompanyDraft): value is CompanyDraft {
  return "createdBy" in value && !("isActive" in value);
}

function isCompany(value: Company | CompanyDraft): value is Company {
  return "isActive" in value;
}

/** Clear the live session only when it still names the slug that just promoted. */
function clearSessionIfPromotedSlug(
  current: DraftSession | null,
  promotedSlug: CompanySlug,
): DraftSession | null {
  if (current === null) return null;
  if (current.draft.slug === promotedSlug) return null;
  return current;
}

/**
 * The draft this screen is editing is gone (deleted, or registered from another
 * session). A retry of the same PATCH cannot succeed, so do not invite one.
 */
const DRAFT_GONE_COPY =
  "This draft is no longer on the server. Abandon it and start again.";

/**
 * Save-draft failure copy, through the one home of failure words. The server's
 * own words are `.detail` only; the create path and the update path speak about
 * different things because a slug collision is never what an update can mean
 * (rd round 11, round 2 review).
 */
function saveDraftFailure(
  error: unknown,
  creating: boolean,
  role: StaffRole,
): FailureCopy {
  const context = { role, context: "write" as const };
  const detail = failureCopy(error, context).detail;
  if (error instanceof AdminApiError) {
    if (creating && error.status === 409) {
      return {
        sentence: "A company or draft already uses this web id.",
        detail,
      };
    }
    if (creating && error.status === 400) {
      return {
        sentence: "The draft was refused. Check the required answers.",
        detail,
      };
    }
    if (!creating && error.status === 404) {
      return { sentence: DRAFT_GONE_COPY, detail };
    }
    if (!creating && error.status === 409) {
      return {
        sentence:
          "The server refused the update — the draft changed; check it and save again.",
        detail,
      };
    }
    if (!creating && error.status === 400) {
      return {
        sentence: "The draft update was refused — check the required answers.",
        detail,
      };
    }
  }
  return { sentence: failureCopy(error, context).sentence, detail };
}

/**
 * Submit-click failure copy (R21O-2 / R21B-1).
 *
 * A completed 4xx did not promote, so decision 1 and the shared write sentences
 * word it. A missing/5xx/network answer is unobserved: the page keeps its own
 * sentence saying the company may exist, because "nothing changed" would be a
 * false claim, and the server's words stay in `.detail` (Show details only).
 */
export const SUBMIT_OUTCOME_UNKNOWN_COPY =
  "Submit did not complete; the company may or may not have been registered — check the companies list before retrying.";

export function humanSubmitFailure(
  error: unknown,
  role: StaffRole,
): FailureCopy {
  const context = { role, context: "write" as const };
  const detail = failureCopy(error, context).detail;
  if (!(error instanceof AdminApiError) || error.status >= 500) {
    return {
      sentence: failureCopy(
        new AuthoredRefusal(SUBMIT_OUTCOME_UNKNOWN_COPY),
        context,
      ).sentence,
      detail,
    };
  }
  if (error.status === 409) {
    return {
      sentence: "A company or draft already uses this web id.",
      detail,
    };
  }
  if (error.status === 404) return { sentence: DRAFT_GONE_COPY, detail };
  return { sentence: failureCopy(error, context).sentence, detail };
}

export function humanSubmitError(
  error: unknown,
  role: StaffRole = "account_manager",
): string {
  return humanSubmitFailure(error, role).sentence;
}

/** UX accept= only. Server mint is the gate (R22A-2). */
export function acceptForOpeningDocumentKind(
  kind: OpeningDocumentKind | "",
): string {
  if (kind === "") return "";
  if (
    kind === "qb-trial-balance" ||
    kind === "qb-balance-sheet" ||
    kind === "qb-profit-and-loss" ||
    kind === "qb-general-ledger"
  ) {
    return ".csv,.txt,text/csv,text/plain";
  }
  return ".pdf,application/pdf";
}

/**
 * A determination PUT refused with 422 (or 400) can never pass by pressing the
 * same button again: the value, the quotation or the year is what the server
 * objects to. Say that, and name the source the AM must check it against. The
 * Worker's own public-safe message goes behind Show details.
 */
export const DETERMINATION_REFUSED_COPY =
  "The server refused this reading. Correct it against the source text.";

/** 409 — another recording won the field. The page reads it back into Show
 * details, and a second press replaces it. Used only when that read produced a
 * detail block to point at. */
export const DETERMINATION_CONFLICT_COPY =
  "Someone else already answered this card. Their answer is in Show details. Your answer is not recorded; press again to replace it.";

/**
 * 409 when the server's record could not be read (failed read, no row for the
 * field, or a client without the list call). Promising Show details the screen
 * cannot show is a dead end, so the sentence stops at what is true (round 3
 * review).
 */
export const DETERMINATION_CONFLICT_NO_DETAIL_COPY =
  "Someone else already answered this card. Your answer is not recorded; press again to replace it.";

/** 404 — the draft is gone, so a retry of the same write cannot succeed. */
export const DETERMINATION_NOT_FOUND_COPY =
  "This draft is no longer on the server. Save the draft again, then record the reading.";

/** A 5xx no longer says "try again, nothing happened": the write may have landed. */
export const DETERMINATION_TRANSIENT_COPY =
  "Could not record the determination on the server — try again.";

export const DETERMINATION_NETWORK_COPY =
  "Could not record the determination on the server — check your connection and try again.";

/**
 * Status-aware copy for a failed determination PUT. A refusal that cannot pass
 * never invites a retry; an unobserved answer never claims nothing happened.
 * The Worker's own words stay in `technicalDetail`, which the banner renders
 * only inside "Show details".
 */
export function humanDeterminationError(
  error: unknown,
  role: StaffRole = "account_manager",
): { copy: string; technicalDetail?: string } {
  const context = { role, context: "write" as const };
  const detail = failureCopy(error, context).detail ?? undefined;
  let sentence: string;
  if (error instanceof AdminApiError) {
    if (error.status === 400 || error.status === 422) {
      sentence = failureCopy(
        new AuthoredRefusal(DETERMINATION_REFUSED_COPY),
        context,
      ).sentence;
    } else if (error.status === 409) {
      sentence = failureCopy(
        new AuthoredRefusal(DETERMINATION_CONFLICT_NO_DETAIL_COPY),
        context,
      ).sentence;
    } else if (error.status === 404) {
      sentence = failureCopy(
        new AuthoredRefusal(DETERMINATION_NOT_FOUND_COPY),
        context,
      ).sentence;
    } else {
      sentence = failureCopy(error, context).sentence;
    }
  } else if (isUnreachable(error)) {
    sentence = failureCopy(
      new AuthoredRefusal(DETERMINATION_NETWORK_COPY),
      context,
    ).sentence;
  } else {
    sentence = failureCopy(error, context).sentence;
  }
  return {
    copy: sentence,
    ...(detail !== undefined ? { technicalDetail: detail } : {}),
  };
}

/** 404 while verifying a quotation: the document is gone, so retrying is not the way out. */
export const QUOTE_VERIFY_DOCUMENT_GONE_COPY =
  "The document is no longer on the server. Upload it again, then quote from it.";

/** Status-aware copy for a verify-quote call that threw. */
export function quoteVerifyFailureCopy(error: unknown): string {
  if (error instanceof AdminApiError && error.status === 404) {
    return QUOTE_VERIFY_DOCUMENT_GONE_COPY;
  }
  return QUOTE_VERIFY_TRANSIENT_REFUSAL;
}

/** Status-aware copy for a crop upload that failed. */
export function humanCropUploadError(
  error: unknown,
  role: StaffRole = "account_manager",
): string {
  const context = { role, context: "write" as const };
  if (error instanceof AdminApiError) {
    if (error.status === 404) {
      return failureCopy(
        new AuthoredRefusal(QUOTE_VERIFY_DOCUMENT_GONE_COPY),
        context,
      ).sentence;
    }
    if (error.status === 400) {
      return failureCopy(
        new AuthoredRefusal("That crop was not accepted. Choose the image again."),
        context,
      ).sentence;
    }
  }
  if (isUnreachable(error)) {
    return failureCopy(
      new AuthoredRefusal(
        "Could not upload the screenshot crop. Check your connection and try again.",
      ),
      context,
    ).sentence;
  }
  return failureCopy(
    new AuthoredRefusal("Could not upload the screenshot crop. Try again."),
    context,
  ).sentence;
}

/** Upload failure copy; the server's words are `.detail` only. */
export function humanUploadFailure(
  error: unknown,
  role: StaffRole = "account_manager",
): FailureCopy {
  const context = { role, context: "write" as const };
  const detail = failureCopy(error, context).detail;
  if (error instanceof AdminApiError && error.status === 404) {
    return {
      sentence: "This company or draft is no longer on the server.",
      detail,
    };
  }
  return { sentence: failureCopy(error, context).sentence, detail };
}

export function humanUploadError(
  error: unknown,
  role: StaffRole = "account_manager",
): string {
  return humanUploadFailure(error, role).sentence;
}

/** Browser-side sha256 of file bytes (declared digest for mint + intake). */
async function sha256HexOfBlob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}
